/* ================================================================
   AISTROWISE — Astromapa teaser (interpretation zone selection)
   Pure client-side: picks which pre-written, astrologer-validated
   units from the static library get shown next to a summary-mode
   /astrocartography-basic response. NO runtime AI.
   PRIVACY: analytics events carry no chart content (structure only).
   Candidates come first from nearest_lines (conjunction lines within
   1.100 km, already ascending by distance) then from natal_angular
   (the fallback used when a chart has no close lines at all — that
   is astrologically honest, never invent a unit to fill the gap).
   Wiring (calling render() from the result screen) is Task 10 — this
   file is unreferenced until then and stays inert.
   ================================================================ */
(function (global) {
  'use strict';
  // Plan inglés (2026-10-20): texto de cliente por awT (identidad en español, aw-dict-en.js en inglés). Los nombres que
  // la API manda en español (planeta, país, continente) pasan por T solo donde se pintan.
  var T = global.awT || function (s) { return s; };
  function isEn() { return global.awLang === 'en'; }
  // Plan francés (2026-10-21): el idioma de la página como parámetro. Español sin cambios (fuera de estas tablas).
  function lang() { return global.awLang || 'es'; }
  function own(o, k) { return Object.prototype.hasOwnProperty.call(o, k) ? o[k] : null; }
  // Display codes of the angles per glossary (English AC and DC, French AS, DS and FC): display only, the data keeps
  // MC/IC/ASC/DSC.
  var ANGLE_CODES = { en: { ASC: 'AC', DSC: 'DC' }, fr: { IC: 'FC', ASC: 'AS', DSC: 'DS' } };
  function angleLabel(a) {
    var u = String(a == null ? '' : a).toUpperCase();
    var m = own(ANGLE_CODES, lang());
    return (m && own(m, u)) || a;
  }
  // Distances outside Spanish: the figure carries its unit ({km} templates say only {km}), thousands separator per
  // language (English 1,045, French 1 045 with a narrow no-break space). Miles only in English (US and UK visitors).
  var THOUSANDS = { en: ',', fr: '\u202f' };
  // The reading's page behind the locks: the waitlist landing outside Spanish.
  var LOCK_LINK = { en: '/en/pages/astrocartography-reading', fr: '/fr/pages/lecture-astrocartographie' };
  // Distancias en inglés: millas para quien visita desde Estados Unidos o Reino Unido (data-aw-country de la
  // sección, Shopify localization), kilómetros para el resto. Lo fijan render() e initCitySearch().
  var MILES = false;
  function setUnits(rootEl) {
    var c = rootEl && rootEl.getAttribute ? String(rootEl.getAttribute('data-aw-country') || '').toUpperCase() : '';
    MILES = c === 'US' || c === 'GB';
  }

  function norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  }
  function slug(s) { return norm(s).replace(/\s+/g, '_'); }

  /* Build the ordered candidate list, skip anything without library
     content, cap at 2, and never use the same planet twice (the
     nearer/first occurrence of a repeated planet wins). Pure — no
     DOM, no globals — so it is directly Node-testable. */
  function selectAstroUnits(summary, lib) {
    var units = (lib && lib.units) || {};
    var candidates = [];

    ((summary && summary.nearest_lines) || []).forEach(function (l) {
      if (!l || !l.planet_es || !l.angle) return;
      candidates.push({
        key: slug(l.planet_es) + '_' + String(l.angle).toLowerCase(),
        planet: norm(l.planet_es),
        params: { km: l.distance_km, angle: l.angle }
      });
    });
    ((summary && summary.natal_angular) || []).forEach(function (l) {
      if (!l || !l.planet_es || !l.angle) return;
      candidates.push({
        key: slug(l.planet_es) + '_' + String(l.angle).toLowerCase(),
        planet: norm(l.planet_es),
        params: {},
        natal: true
      });
    });

    var chosen = [], usedPlanets = {};
    for (var i = 0; i < candidates.length && chosen.length < 2; i++) {
      var c = candidates[i];
      var entry = units[c.key];
      if (!entry) continue; // no library content for this fact: skip, never invent
      // Natal-mode safety: a natal_angular candidate without BOTH natal-framed
      // variants must never render — a zone/distance body under a natal
      // anchor (or vice versa) is an astrological self-contradiction. Skip
      // the whole unit rather than invent or mix framings.
      if (c.natal && (!entry.anchor_natal || !entry.body_natal)) continue;
      if (usedPlanets[c.planet]) continue; // planet diversity: max 1 unit per planet
      usedPlanets[c.planet] = true;
      chosen.push({ key: c.key, params: c.params, entry: entry });
    }
    return chosen;
  }

  /* Guaranteed base unit (D4b): keyed by the engine's own display bands
     (the only possible birth displays are 0-29, exactly 35, and 45-100),
     branched by presence of conjunction lines within 1.100 km. Pure —
     returns null for score 0/invalid (indistinguishable from a failed
     computation) and for the impossible 30-44 band (defensive: never
     narrate a band the engine cannot produce). */
  function pickBaseUnit(summary, lib) {
    var base = lib && lib.base;
    if (!base) return null;
    var birth = (summary && summary.birth_city) || {};
    var score = Number(birth.score);
    if (birth.score == null || !isFinite(score) || score < 0) return null;
    var near = ((summary && summary.nearest_lines) || []).some(function (l) {
      return l && l.distance_km != null && Number(l.distance_km) <= 1100;
    });
    var key = null;
    if (score < 30) key = near ? 'exigente_lineas' : 'exigente_sin';
    else if (score === 35) key = 'neutra';
    else if (score >= 45 && score < 70) key = near ? 'media_lineas' : 'media_sin';
    else if (score >= 70) key = near ? 'alta_lineas' : 'alta_sin';
    if (!key) return null;
    var entry = base[key];
    if (!entry || !entry.body) return null;
    return { key: key, score: score, body: entry.body };
  }

  /* Doors arithmetic (hard rule: doors never outnumber readable units).
     Pure — returns which locked units may render, in DISPLAY order
     (gap first, zone last). Selection priority when only one fits is
     the zone door. The zone door also needs its continent to be
     provably distinct from the top city's: runners_up are the best of
     OTHER continents, so a continent match guarantees distinctness;
     no match means it could be the top city's own continent (a visible
     self-contradiction two cards apart) and the door is suppressed. */
  function validRunner(r) {
    return !!(r && r.continent_es != null && String(r.continent_es).trim() !== '' && r.score != null && isFinite(Number(r.score)));
  }
  /* Brujula de objetivos (7-sep): el candado de tres terrenos (cuatro desde el 18-sep) que sustituye
     al hueco entre continentes. El mapa terreno -> planeta es CONSTANTE: no
     se sustituye un significador por otro para rellenar una fila. */
  var BRUJULA_ROWS = [
    { terreno: 'amor',     planeta: 'venus',   label: 'brujulaAmorLabel',     line: 'brujulaAmorLine',     payoff: 'brujulaAmorPayoff',     pl: 'brujulaPlanetaAmor',     gl: 'brujulaAmorGlifo',     dato: 'brujulaAmorDato' },
    { terreno: 'carrera',  planeta: 'sol',     label: 'brujulaCarreraLabel',  line: 'brujulaCarreraLine',  payoff: 'brujulaCarreraPayoff',  pl: 'brujulaPlanetaCarrera',  gl: 'brujulaCarreraGlifo',  dato: 'brujulaCarreraDato' },
    { terreno: 'recursos', planeta: 'jupiter', label: 'brujulaRecursosLabel', line: 'brujulaRecursosLine', payoff: 'brujulaRecursosPayoff', pl: 'brujulaPlanetaRecursos', gl: 'brujulaRecursosGlifo', dato: 'brujulaRecursosDato' },
    // 18-sep (founder): la cuarta fila del informe, «Refugios para tu bienestar y energía»
    { terreno: 'bienestar', planeta: 'luna',   label: 'brujulaBienestarLabel', line: 'brujulaBienestarLine', payoff: 'brujulaBienestarPayoff', pl: 'brujulaPlanetaBienestar', gl: 'brujulaBienestarGlifo', dato: 'brujulaBienestarDato' }
  ];
  /* Planeta de una unidad legible DE LINEA. La clave es slug(planeta) + '_' +
     angulo y hay slugs con guion bajo (nodo_norte), asi que se corta por el
     ULTIMO. Solo cuentan candidatos de linea: natal_angular llega con params
     sin km y habla de un rasgo constante, no de geografia. */
  function pickedLinePlanets(picked) {
    var out = {};
    (picked || []).forEach(function (u) {
      var k = String((u && u.key) || '');
      var i = k.lastIndexOf('_');
      if (i > 0 && u.params && u.params.km != null) out[k.slice(0, i)] = true;
    });
    return out;
  }
  /* Zona por terreno (10-sep, directiva del founder: «tiene que contarle algo
     sin contarle tanto»). La API sirve en area_zones el continente de la
     ciudad que mas puntua en cada terreno, y cada fila abre con una revelacion
     real de SU carta («hay una franja de {continente} donde...»), elegida
     entre las variantes del bloque i18n de forma determinista por carta: la
     misma carta lee siempre la misma frase, dos cartas distintas leen frases
     distintas. Solo afirmaciones de existencia: el informe escanea un pool
     mayor y su numero 1 puede cambiar. Sin TODOS los continentes, todas las
     filas vuelven a la linea generica de hoy: media brujula es media promesa. */
  var ZONA_TPLS = { amor: 'brujulaAmorZonaTpls', carrera: 'brujulaCarreraZonaTpls', recursos: 'brujulaRecursosZonaTpls', bienestar: 'brujulaBienestarZonaTpls' };
  function areaZones(summary) {
    var z = summary && summary.area_zones;
    if (!z) return null;
    var out = {};
    for (var i = 0; i < BRUJULA_ROWS.length; i++) {
      var t = BRUJULA_ROWS[i].terreno;
      var c = z[t] && z[t].continent_es != null ? String(z[t].continent_es).trim() : '';
      if (!c) return null;
      out[t] = c;
    }
    return out;
  }
  // Semilla estable por carta a partir de lo que ya viaja en el resumen (sin
  // datos de nacimiento): misma carta, misma frase en cada recarga.
  function chartSeed(summary) {
    var tc = (summary && summary.top_city) || {}, b = (summary && summary.birth_city) || {};
    var zones = areaZones(summary) || {};
    var s = [tc.name, tc.score, b.name, b.score, zones.amor, zones.carrera, zones.recursos].join('|');
    // Jenkins one-at-a-time: un multiplicador simple (31 = 1 mod 6) dejaba el
    // modulo 6 en manos de la suma de caracteres y solo repartia 3 variantes.
    var h = 0;
    for (var i = 0; i < s.length; i++) {
      h = (h + s.charCodeAt(i)) | 0;
      h = (h + (h << 10)) | 0;
      h ^= h >>> 6;
    }
    h = (h + (h << 3)) | 0;
    h ^= h >>> 11;
    h = (h + (h << 15)) | 0;
    return h >>> 0;
  }
  // Las variantes de cada terreno comparten arranque por indice («Hay una
  // franja de», «En X hay», «Existe», ...). Con cuatro filas (18-sep) se avanza
  // de 1 en 1 sobre 6: cuatro indices distintos, asi que ni con el MISMO
  // continente en todos los terrenos abren dos filas igual (de 2 en 2, la
  // cuarta repetia el arranque de la primera). Devuelve null si a algun
  // terreno le falta la lista o la variante.
  function zonaLines(i18n, zones, seed) {
    if (!zones) return null;
    var out = {};
    for (var i = 0; i < BRUJULA_ROWS.length; i++) {
      var t = BRUJULA_ROWS[i].terreno, tpls = i18n[ZONA_TPLS[t]];
      if (!tpls || !tpls.length) return null;
      var cz = T(zones[t]);
      var line = fillText(tpls[(seed + i) % tpls.length], { continente: cz });
      if (!line || line.indexOf(cz) < 0) return null;
      out[t] = line;
    }
    return out;
  }
  /* TODO O NADA: media brujula es media promesa, y la linea de seccion nombra
     los cuatro terrenos. Si falta una sola cadena, no hay candado. Con zona
     por terreno la fila lleva la revelacion en vez del dato curioso (el dato
     era igual para todo el mundo, la revelacion no). */
  function brujulaRows(i18n, picked, summary) {
    i18n = i18n || {};
    var echo = pickedLinePlanets(picked), rows = [];
    var zonas = zonaLines(i18n, areaZones(summary), chartSeed(summary));
    for (var i = 0; i < BRUJULA_ROWS.length; i++) {
      var r = BRUJULA_ROWS[i];
      var label = i18n[r.label], payoff = i18n[r.payoff];
      var glifo = i18n[r.gl];
      var line = zonas ? zonas[r.terreno] : i18n[r.line];
      var dato = zonas ? '' : i18n[r.dato];
      var isEcho = !!echo[r.planeta];
      // El informe ya no lee un área por un solo planeta (matriz de áreas, 2026-10-29): sin la etiqueta «por tu Venus».
      if (!label || !line || !payoff || !glifo || (!zonas && !dato)) return [];
      rows.push({ terreno: r.terreno, glifo: glifo, label: label, line: line, dato: dato, zona: !!zonas,
                  payoff: payoff, echo: isEcho });
    }
    // Orden estable: la fila cuyo regente ya salio como linea legible sube primero.
    return rows.filter(function (r) { return r.echo; }).concat(rows.filter(function (r) { return !r.echo; }));
  }
  function brujulaOkFor(i18n, picked, summary) {
    return !!(i18n && i18n.brujulaSectionLine && i18n.lockedBrujulaPlainTpl && i18n.lockLine) &&
      brujulaRows(i18n, picked, summary).length === BRUJULA_ROWS.length;
  }
  /* Ancla del candado: tres peldanos, ninguno inventa nada. Con carta del pico
     reencuadra la cifra que ya esta en pantalla; en birthMax usa la natal y no
     nombra ciudad (la carta del pico tampoco lo hace ahi); sin carta del pico
     cae a la variante sin tokens, que no puede imprimir un hueco.
     Devuelve HTML: fillTpl ya escapa la ciudad, así que va tal cual a
     lockedUnitHtml. Un esc() encima pintaba «St. John&#39;s» (11-sep). */
  function brujulaAnchor(summary, i18n, hayTopCard) {
    i18n = i18n || {};
    var tc = (summary && summary.top_city) || {};
    var birth = (summary && summary.birth_city) || {};
    var tcOk = tc.name != null && tc.score != null && isFinite(Number(tc.score));
    if (!hayTopCard || !tcOk) return i18n.lockedBrujulaPlainTpl || '';
    var birthMax = birth.score != null && isFinite(Number(birth.score)) && Number(tc.score) <= Number(birth.score);
    if (birthMax) {
      return i18n.lockedBrujulaBirthTpl
        ? fillTpl(i18n.lockedBrujulaBirthTpl, { score: birth.score })
        : (i18n.lockedBrujulaPlainTpl || '');
    }
    return i18n.lockedBrujulaTpl
      ? fillTpl(i18n.lockedBrujulaTpl, { ciudad: T(tc.name), score: tc.score })
      : (i18n.lockedBrujulaPlainTpl || '');
  }
  function selectAstroDoors(summary, readableCount, brujulaOk, separatorOk) {
    if (!summary) return [];
    var cap = Math.min(readableCount > 0 ? readableCount : 0, 2); // G5 intacta
    if (cap === 0) return [];
    var runnersUp = summary.runners_up || [];
    var zone = summary.challenge_zone;
    var zoneContinent = zone && zone.continent_es != null ? String(zone.continent_es).trim() : '';
    var zoneOk = zoneContinent !== '' && runnersUp.some(function (r) {
      return validRunner(r) && norm(r.continent_es) === norm(zoneContinent);
    });
    // Orden de DOM: zone va antes de la carta del pico y brujula despues.
    var doors = [];
    if (zoneOk) doors.push('zone');
    if (brujulaOk) doors.push('brujula');
    // Con un solo hueco gana brujula: se conservan las ultimas `cap` puertas.
    if (doors.length > cap) doors = doors.slice(doors.length - cap);
    // La carta del pico es el UNICO separador posible entre dos candados.
    if (doors.length === 2 && !separatorOk) doors = ['brujula'];
    return doors;
  }

  /* ── browser wiring (no-op under Node) ──────────────────────── */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // Integer km, thousands separated with a dot: 1045 -> "1.045".
  function fmtKm(km) {
    var n = Math.round(Number(km));
    if (!isFinite(n)) return '';
    var s = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (n < 0 ? '-' : '') + s;
  }
  /* ASC/DSC ground distances come from sampling the in-mundo horizon
     curve every 2°: near a bend the honest precision is ~50 km, so they
     render rounded to 50 with an "unos" hedge ("pasa a unos 350 km").
     MC/IC use the exact meridian formula and keep the integer. */
  function fmtDistance(km, angle) {
    var a = String(angle || '').toUpperCase();
    if (a === 'ASC' || a === 'DSC') {
      var n = Math.round(Number(km));
      if (!isFinite(n)) return '';
      return 'unos ' + fmtKm(Math.max(50, Math.round(n / 50) * 50));
    }
    return fmtKm(km);
  }
  /* Inglés: la cifra lleva su unidad (las plantillas inglesas dicen solo {km}): "1,045 km", "about 225 miles".
     MC/IC entera, ASC/DSC redondeada como en español (50 km, o 25 millas) y con su "about". */
  function fmtDistanceEn(km, angle, miles) {
    var a = String(angle || '').toUpperCase();
    var v = Number(km);
    if (!isFinite(v)) return '';
    if (miles) v = v * 0.621371;
    var step = miles ? 25 : 50, approx = a === 'ASC' || a === 'DSC';
    var r = approx ? Math.max(step, Math.round(v / step) * step) : Math.round(v);
    var n = (r < 0 ? '-' : '') + String(Math.abs(r)).replace(/\B(?=(\d{3})+(?!\d))/g, own(THOUSANDS, lang()) || ',');
    var tpl = miles ? (approx ? T('unos {n} millas') : (r === 1 ? T('{n} milla') : T('{n} millas')))
      : (approx ? T('unos {n} km') : T('{n} km'));
    return fillText(tpl, { n: n });
  }
  function kmText(km, angle) { return own(THOUSANDS, lang()) ? fmtDistanceEn(km, angle, isEn() && MILES) : fmtDistance(km, angle); }
  function fillAnchor(tpl, params) {
    var km = params && params.km != null ? esc(kmText(params.km, params.angle)) : '';
    return String(tpl || '').replace(/\{km\}/g, km);
  }
  // Generic {token} filler for the locked-unit i18n templates. Every
  // substituted value is esc()d; the template text itself is trusted
  // static copy (section settings), so it is left as-is.
  function fillTpl(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) {
      var v = map && map[k];
      return v != null ? esc(String(v)) : '';
    });
  }
  // params (11-sep): solo claves de estructura, hoy lock_key en los eventos de la
  // compra en el candado. Nunca datos de carta ni de nacimiento.
  function pushEvent(name, params) {
    var payload = { event: name };
    if (params) { for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k) && k !== 'event') payload[k] = params[k]; } }
    try {
      global.dataLayer = global.dataLayer || [];
      global.dataLayer.push(payload);
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('aw:track', { detail: { name: name } })); } catch (e) {}
  }

  /* ── suspense helpers (inert unless the section carries the attribute) ── */
  function suspenseOn(rootEl) {
    return !!(rootEl && rootEl.hasAttribute && rootEl.hasAttribute('data-aw-astro-suspense'));
  }
  function prefersReduced() {
    try { return global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }
  function isLite() {
    try {
      var n = global.navigator || {};
      return (n.hardwareConcurrency && n.hardwareConcurrency <= 4) || (n.deviceMemory && n.deviceMemory <= 2);
    } catch (e) { return false; }
  }
  // Integer count-up, 550 ms easeOutCubic. Falls back to the final value.
  function countUpEl(el, finalValue, ms) {
    try {
      var start = null;
      var step = function (ts) {
        if (start == null) start = ts;
        var p = Math.min(1, (ts - start) / (ms || 550));
        var e = 1 - Math.pow(1 - p, 3);
        el.textContent = String(Math.round(finalValue * e));
        if (p < 1) global.requestAnimationFrame(step);
      };
      global.requestAnimationFrame(step);
    } catch (e) { el.textContent = String(finalValue); }
  }
  function growBar(fillEl) {
    fillEl.classList.add('is-scale');
    try {
      global.requestAnimationFrame(function () {
        global.requestAnimationFrame(function () { fillEl.classList.add('is-grown'); });
      });
    } catch (e) { fillEl.classList.add('is-grown'); }
  }

  // {km} anchors degrade to "pasa a  km" for natal_angular candidates
  // (params.km is null there): prefer entry.anchor_natal when present.
  function pickAnchor(entry, params) {
    return (params && params.km == null && entry && entry.anchor_natal) ? entry.anchor_natal : (entry && entry.anchor);
  }
  // {km}-anchored bodies describe a distance/zone framing that doesn't apply
  // to a natal_angular unit (params.km is null there): prefer entry.body_natal
  // (constant-trait framing) when present. selectAstroUnits already refuses
  // to choose a natal candidate whose entry lacks body_natal, so this is a
  // same-selection-logic mirror of pickAnchor, not an independent guard.
  function pickBody(entry, params) {
    return (params && params.km == null && entry && entry.body_natal) ? entry.body_natal : (entry && entry.body);
  }
  /* Badge tying each readable unit to the exact line it interprets: the
     planet glyph in its MAP color plus the angle's real stroke pattern.
     Reads window.AWAstroMap (single source of truth exposed by
     aw-astromapa.js) — never a duplicated table, so a future color change
     can not desynchronize badge and map. Absent (Node, partial sync):
     no badge, unit renders as before. Decorative only (aria-hidden):
     planet and angle are already in the anchor TEXT. */
  function unitBadgeHtml(u) {
    var M = global.AWAstroMap;
    if (!M || !M.PLANETS || !u || !u.key) return '';
    var key = String(u.key);
    var cut = key.lastIndexOf('_');
    if (cut < 1) return '';
    var pKey = key.slice(0, cut);
    var aCode = key.slice(cut + 1).toUpperCase();
    var planet = null;
    for (var i = 0; i < M.PLANETS.length; i++) {
      if (M.PLANETS[i].keys && M.PLANETS[i].keys.indexOf(pKey) > -1) { planet = M.PLANETS[i]; break; }
    }
    if (!planet) return '';
    var angle = null;
    if (M.ANGLES) {
      for (var j = 0; j < M.ANGLES.length; j++) {
        if (M.ANGLES[j].code === aCode) { angle = M.ANGLES[j]; break; }
      }
    }
    var swatch = angle
      ? '<svg class="aw-astro__unit-dash" width="22" height="6" viewBox="0 0 22 6" aria-hidden="true"><line x1="1" y1="3" x2="21" y2="3" stroke="' + esc(planet.color) + '" stroke-width="2"' + (angle.dash ? ' stroke-dasharray="' + esc(angle.dash) + '"' : '') + '></line></svg>'
      : '';
    return '<span class="aw-astro__unit-glyph" aria-hidden="true">' +
      '<i class="aw-astro__unit-disc" style="color:' + esc(planet.color) + '">' + planet.glyph + '︎</i>' +
      swatch + '</span>';
  }

  // Optional insight titular (library v2 schema, natal v2 position 9): when
  // the entry carries one (titular_natal for the natal framing), it leads as
  // the h3 and the anchor demotes to the fact line. Without it, the markup
  // is byte-identical to today's (no titulares are written in this batch).
  function pickTitular(entry, params) {
    var t = (params && params.km == null && entry && entry.titular_natal) ? entry.titular_natal : (entry && entry.titular);
    return t != null && String(t).trim() ? String(t).trim() : '';
  }
  // Glosa (2026-10-12): what the line, the planet and the angle are, under the
  // fact. A natal_angular unit (no km) skips the "line" sentence. Empty without
  // aw-glosa.js.
  function astroGlosaHtml(u, seen) {
    var G = global.AWGlosa;
    var g = G && G.astroLinea && u && u.key ? G.astroLinea(u.key, !(u.params && u.params.km == null), seen) : '';
    return g ? '<p class="aw-astro__unit-gloss">' + esc(g) + '</p>' : '';
  }
  function readableUnitHtml(u, seen) {
    var titular = pickTitular(u.entry, u.params);
    var anchorHtml = unitBadgeHtml(u) + '<span>' + fillAnchor(pickAnchor(u.entry, u.params), u.params) + '</span>';
    var body = '<p class="aw-astro__unit-body">' + esc(pickBody(u.entry, u.params)) + '</p>';
    // Insight first (2026-10-12): titular and body, then the fact and its glosa.
    return '<article class="aw-astro__unit">' +
      (titular
        ? '<h3 class="aw-astro__unit-titular">' + esc(titular) + '</h3>' + body +
          '<p class="aw-astro__unit-fact aw-astro__unit-anchor">' + anchorHtml + '</p>' + astroGlosaHtml(u, seen)
        : '<h3 class="aw-astro__unit-anchor">' + anchorHtml + '</h3>' + body) +
      '</article>';
  }
  // Locked units NEVER carry real interpretive text — only the anchor
  // (built from chart-shaped but non-revealing summary fields), a chip,
  // two decorative bars standing in for the hidden paragraph, and the
  // line naming the REAL paid-report section where it is read.
  function lockedUnitHtml(anchorHtml, opts) {
    opts = opts || {};
    // Handoff (2026-09-01): the lock key travels in the href (?candado=) and
    // in the event payload (data-awp-lock-key), and the approved sectionLine
    // rides on a data attribute so the landing can reopen exactly where the
    // lock closed. Library copy only — never chart data.
    var href = opts.key ? opts.link + '?candado=' + encodeURIComponent(opts.key) : opts.link;
    // Brújula (18-sep, founder): su línea de sección ya no se pinta, pero sigue
    // viajando en el handoff (opts.handoff) para que la landing abra en su sitio.
    var handoff = opts.handoff != null ? opts.handoff : (opts.sectionLine || '');
    // Filas selladas (7-sep): cuando llegan, el par decorativo de barras se
    // sustituye por una lista de <li>, cada uno con SU barra. Son li, nunca
    // .aw-astro__unit, asi que el contador de legibles no las ve y G5 no
    // cambia de definicion. Sin rows, el candado es byte por byte el de hoy.
    var rows = opts.rows && opts.rows.length ? opts.rows : null;
    var cuerpo = rows
      ? '<ul class="aw-astro__lock-rows">' + rows.map(function (r) {
          return '<li class="aw-astro__lock-row"' + (r.terreno ? ' data-aw-terreno="' + esc(r.terreno) + '"' : '') + '>' +
            '<span class="aw-astro__lock-row-label">' +
              (r.glifo ? '<span class="aw-astro__lock-row-glifo" aria-hidden="true">' + esc(r.glifo) + '</span>' : '') +
              esc(r.label) + '</span>' +
            '<p class="aw-astro__lock-row-line">' + esc(r.line) + '</p>' +
            (r.tag ? '<p class="aw-astro__lock-row-tag">' + esc(r.tag) + '</p>' : '') +
            (r.dato ? '<p class="aw-astro__lock-row-dato">' + esc(r.dato) + '</p>' : '') +
            '<p class="aw-astro__lock-row-payoff">' + esc(r.payoff) + '</p>' +
            '<div class="aw-astro__bars" aria-hidden="true"><i></i></div>' +
          '</li>';
        }).join('') + '</ul>'
      : '<div class="aw-astro__bars" aria-hidden="true"><i></i><i></i></div>';
    return '<article class="aw-astro__unit aw-astro__unit--locked' + (rows ? ' aw-astro__unit--rows' : '') + '">' +
      (opts.chip ? '<span class="aw-astro__lock-chip">' + esc(opts.chip) + '</span>' : '') +
      '<h3 class="aw-astro__unit-anchor">' + anchorHtml + '</h3>' +
      cuerpo +
      (opts.sectionLine ? '<p class="aw-astro__lock-section">' + esc(opts.sectionLine) + '</p>' : '') +
      '<a class="aw-astro__lock-link" href="' + esc(href) + '" data-aw-event="astro_lock_click"' +
      (opts.key ? ' data-awp-lock-key="' + esc(opts.key) + '" data-aw-handoff-key="' + esc(opts.key) + '" data-aw-handoff-seccion="' + esc(handoff) + '"' : '') +
      '>' + esc(opts.lockLine) + '</a>' +
      '</article>';
  }

  /* Compra en el candado (2026-09-11, decisión del founder). Con la palanca
     candado_inline_buy, el clic en un candado abre este panel justo debajo del
     enlace en vez de llevar a la landing (llegadas por candado: 1 carrito en
     142 sesiones). Solo copy ya aprobado: la cabecera cita el propio candado tal
     como se pintó, las dos líneas son las del bloque de compra, la cifra la
     pinta Shopify (aw-price desde el i18n, confianza de tema, entra tal cual) y
     el form es el mismo /cart/add de la caja de compra de la landing, que
     intercepta aw-cart-add.js. Sin i18n completo no hay panel y el candado
     navega como siempre. Constructores puros, probados en Node. */
  function buyReady(i18n) {
    return !!(i18n && i18n.buyVariant && String(i18n.buyPrice || '').trim() && i18n.buyCta && i18n.buyAction);
  }
  function buyHead(anchorText, sectionLine) {
    var t = String(anchorText == null ? '' : anchorText).replace(/\s+/g, ' ').trim();
    return t ? '«' + t + '»' : String(sectionLine == null ? '' : sectionLine).trim();
  }
  function buyPanelHtml(o) {
    o = o || {};
    var lines = (o.lines || []).filter(function (l) { return l != null && String(l).trim() !== ''; });
    return '<div class="aw-astro__buy" id="' + esc(o.id) + '" data-aw-astro-buy="' + esc(o.key) + '" role="group" aria-label="' + esc(o.title) + '">' +
      '<div class="aw-astro__buy-top">' +
        (o.lead ? '<span class="aw-astro__lock-chip">' + esc(o.lead) + '</span>' : '') +
        '<button type="button" class="aw-astro__buy-close" data-aw-astro-buy-close aria-label="' + esc(o.closeLabel) + '">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="M2.5 2.5l9 9M11.5 2.5l-9 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>' +
        '</button>' +
      '</div>' +
      (o.head ? '<p class="aw-astro__buy-head" data-aw-astro-buy-head>' + esc(o.head) + '</p>' : '') +
      lines.map(function (l) { return '<p class="aw-astro__buy-line">' + esc(l) + '</p>'; }).join('') +
      '<div class="aw-astro__buy-price"><span class="aw-astro__buy-name">' + esc(o.title) + '</span>' + String(o.priceHtml || '') + '</div>' +
      String(o.noteHtml || '') +
      // Las opiniones bajo el nombre (founder, 14-oct): markup del tema (snippets/aw-card-reviews.liquid), nunca de aquí.
      String(o.reviewsHtml || '') +
      '<form action="' + esc(o.action) + '" method="post" class="aw-astro__buy-form" data-aw-astro-buy-form>' +
        '<input type="hidden" name="id" value="' + esc(o.variant) + '">' +
        '<button type="submit" name="add" class="aw-astro__btn"' + (o.available === false ? ' disabled' : '') + '>' + esc(o.cta) + '</button>' +
      '</form>' +
      (o.moreHref && o.moreLabel ? '<a class="aw-astro__buy-more" href="' + esc(o.moreHref) + '" data-aw-astro-buy-more="' + esc(o.key) + '">' + esc(o.moreLabel) + '</a>' : '') +
      '</div>';
  }
  // Payload que la franja de la landing lee (2026-09-01): copy de librería y claves de
  // estructura. sessionStorage puede lanzar (modo privado) y el clic nunca se rompe.
  function writeHandoff(a) {
    if (!a) return;
    try {
      sessionStorage.setItem('aw_lock_handoff', JSON.stringify({
        calc: 'astro', key: a.getAttribute('data-aw-handoff-key'),
        seccion: a.getAttribute('data-aw-handoff-seccion') || '',
        hook: a.getAttribute('data-aw-handoff-hook') || undefined
      }));
    } catch (err) {}
  }
  function closeBuyPanel(scopeEl, focusBack) {
    var p = scopeEl ? scopeEl.querySelector('[data-aw-astro-buy]') : null;
    if (!p) return;
    var a = p.previousElementSibling;
    p.parentNode.removeChild(p);
    if (!a || !a.hasAttribute('data-aw-handoff-key')) return;
    a.setAttribute('aria-expanded', 'false');
    a.removeAttribute('aria-controls');
    if (focusBack) { try { a.focus(); } catch (e) {} }
  }
  function openBuyPanel(rootEl, scopeEl, a, i18n) {
    var top0 = a.getBoundingClientRect().top;
    closeBuyPanel(scopeEl, false); // uno solo abierto
    var key = a.getAttribute('data-aw-handoff-key') || '';
    var card = a.closest('.aw-astro__unit--locked');
    var anchor = card ? card.querySelector('.aw-astro__unit-anchor') : null;
    var id = 'aw-astro-buy-' + (rootEl.getAttribute('data-section-id') || 'x') + '-' + key;
    a.insertAdjacentHTML('afterend', buyPanelHtml({
      id: id, key: key, lead: i18n.buyLead, closeLabel: i18n.buyClose,
      head: buyHead(anchor ? anchor.textContent : '', a.getAttribute('data-aw-handoff-seccion')),
      lines: [i18n.bridgeCloseShort],
      title: i18n.buyTitle, priceHtml: i18n.buyPrice, noteHtml: i18n.buyNote, reviewsHtml: i18n.buyReviews || '',
      action: i18n.buyAction, variant: i18n.buyVariant, available: i18n.buyAvailable,
      cta: i18n.buyCta, moreHref: a.getAttribute('href'), moreLabel: i18n.inv_more
    }));
    a.setAttribute('aria-expanded', 'true');
    a.setAttribute('aria-controls', id);
    // Sin salto: si se cerró un panel de más arriba, el candado pulsado vuelve a su
    // sitio en pantalla. Después, solo si el panel queda bajo el borde, el
    // desplazamiento mínimo para verlo entero (scroll-margin salva el banner de cookies).
    var d = a.getBoundingClientRect().top - top0;
    if (d) { try { global.scrollBy(0, d); } catch (e) {} }
    var p = a.nextElementSibling;
    var vh = global.innerHeight || document.documentElement.clientHeight;
    if (p && p.getBoundingClientRect().bottom > vh) {
      try { p.scrollIntoView({ block: 'nearest', behavior: prefersReduced() ? 'auto' : 'smooth' }); } catch (e) {}
    }
    pushEvent('astro_candado_panel_open', { lock_key: key });
  }

  /* Peak card ("Tu punto más alto"): late reveal, no lock, no hero
     numeral. Pure string builder — Node-testable. Guard: when no famous
     city beats the birth city (the birth city is in the normalization
     pool but not in famous_analyzed), the honest variant renders instead
     of a card that contradicts tile 1 two cards up. */
  function topCardHtml(summary, i18n) {
    var tc = (summary && summary.top_city) || {};
    var birth = (summary && summary.birth_city) || {};
    if (tc.name == null || tc.score == null || !isFinite(Number(tc.score))) return '';
    var birthMax = birth.score != null && isFinite(Number(birth.score)) && Number(tc.score) <= Number(birth.score);
    var body = birthMax
      ? (i18n.topCardBirthMax || '')
      : fillTpl(i18n.topCardTpl, { ciudad: T(tc.name), pais: tc.country_es ? T(tc.country_es) : '', score: tc.score });
    if (!body) return '';
    return '<article class="aw-astro__topcard" data-aw-astro-topcard>' +
      '<p class="aw-astro__topcard-kicker">' + esc(i18n.topCardTitle || '') + '</p>' +
      (birthMax ? '' :
        '<h3 class="aw-astro__topcard-city">' + esc(T(tc.name)) + '</h3>' +
        (tc.country_es ? '<p class="aw-astro__topcard-country">' + esc(T(tc.country_es)) + '</p>' : '')) +
      '<p class="aw-astro__topcard-body">' + body + '</p>' +
      '</article>';
  }

  /* ── session receipt on the final bridge (P1, 2026-09-05) ──
     The bridge card is the computed receipt of this session: what she put
     in (the echo sentence) and what this page leaves open (an ordered list
     built ONLY from data that exists). Zero client-side kilometres, zero
     prices. The only numbers are the natal score and the two runner-up
     scores, all in the summary's own normalization and all under the scale
     frame. Searched-city scores are NEVER printed here: each search is
     normalized with its own city inside the pool, so those numbers share
     no scale. Page state only (searched cities are not persisted). */
  // Per-root session state (a page may stamp several wizards, the harness
  // does): searched cities, the area-line ownership flag and the last
  // painted summary/units/doors for receipt refreshes after a city result.
  function sessionFor(rootEl) {
    var blank = { searched: [], areaLineShown: false, brujulaShown: false, receipt: { summary: null, picked: [], doors: [] } };
    if (!rootEl) return blank;
    if (!rootEl._awAstroSession) rootEl._awAstroSession = blank;
    return rootEl._awAstroSession;
  }
  var receiptEvtFired = false; // once per page load

  // Raw {token} filler for textContent targets (no escaping: the value
  // never reaches an HTML parser, so an apostrophe stays an apostrophe).
  function fillText(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) {
      var v = map && map[k];
      return v != null ? String(v) : '';
    });
  }
  // "A, B y C" with the i18n separators (defaults kept for partial syncs).
  function joinList(items, sep, last) {
    var arr = (items || []).filter(function (x) { return x != null && String(x) !== ''; }).map(String);
    if (!arr.length) return '';
    if (arr.length === 1) return arr[0];
    return arr.slice(0, -1).join(sep == null ? ', ' : sep) + (last == null ? T(' y ') : last) + arr[arr.length - 1];
  }
  // "Neptuno DSC" from a library key, via the map's own planet table (the
  // same lookup unitBadgeHtml uses). '' when AWAstroMap is absent: omit.
  function unitLineName(u) {
    var M = global.AWAstroMap;
    if (!M || !M.PLANETS || !u || !u.key) return '';
    var key = String(u.key);
    var cut = key.lastIndexOf('_');
    if (cut < 1) return '';
    var pKey = key.slice(0, cut);
    var aCode = key.slice(cut + 1).toUpperCase();
    for (var i = 0; i < M.PLANETS.length; i++) {
      if (M.PLANETS[i].keys && M.PLANETS[i].keys.indexOf(pKey) > -1) return T(M.PLANETS[i].es) + ' ' + angleLabel(aCode);
    }
    return '';
  }
  function birthScoreOf(summary) {
    var birth = (summary && summary.birth_city) || {};
    var s = Number(birth.score);
    return birth.score != null && isFinite(s) && s >= 0 ? s : null;
  }
  /* Pure: the open-items list in page order, each item only when its data
     exists. lineNames are already resolved ("Venus DSC"), doors come from
     selectAstroDoors, searched are the autocomplete names in typing order.
     Never sorted by score, never a superlative, never a searched score. */
  function buildReceiptItems(summary, lineNames, doors, searched, i18n) {
    i18n = i18n || {};
    var items = [];
    var score = birthScoreOf(summary);
    if (score != null) {
      var natalTpl = i18n.cityName ? i18n.bridgeOpenBirth : i18n.bridgeOpenBirthNoCity;
      if (natalTpl) items.push(fillText(natalTpl, { ciudad: i18n.cityName, score: score }));
    }
    // Open items promise closure in the reading, and the reading's map draws
    // ten bodies: a Quirón line (drawn on the free map only) never becomes
    // an open item. The echo sentence keeps it (a fact about this page).
    var names = (lineNames || []).filter(Boolean).filter(function (n) { return !/^(?:Quir[oó]n|Chiron)\b/.test(n); }).slice(0, 2);
    if (names.length === 1 && i18n.bridgeOpenLine1) items.push(fillText(i18n.bridgeOpenLine1, { l1: names[0] }));
    else if (names.length === 2 && i18n.bridgeOpenLine2) items.push(fillText(i18n.bridgeOpenLine2, { l1: names[0], l2: names[1] }));
    var d = doors || [];
    if (d.indexOf('brujula') > -1 && i18n.bridgeOpenBrujula) items.push(i18n.bridgeOpenBrujula);
    if (d.indexOf('zone') > -1 && i18n.bridgeOpenZone) items.push(i18n.bridgeOpenZone);
    var cities = (searched || []).filter(Boolean).slice(0, 3);
    if (cities.length && i18n.bridgeOpenSearch) items.push(fillText(i18n.bridgeOpenSearch, { ciudades: joinList(cities, i18n.bridgeListSep, i18n.bridgeListLast) }));
    return items;
  }
  /* Pure: entry echo by priority: searched cities > featured line (no
     distance, no superlative: the featured unit is not necessarily the
     nearest) > natal score > the default sentence. */
  function pickBridgeEcho(summary, lineNames, searched, i18n) {
    i18n = i18n || {};
    var cities = (searched || []).filter(Boolean).slice(0, 3);
    if (cities.length === 1 && i18n.bridgeEchoSearch1) return fillText(i18n.bridgeEchoSearch1, { ciudad: cities[0] });
    if (cities.length >= 2 && i18n.bridgeEchoSearchN) return fillText(i18n.bridgeEchoSearchN, { ciudades: joinList(cities, i18n.bridgeListSep, i18n.bridgeListLast) });
    var names = (lineNames || []).filter(Boolean);
    if (names.length && i18n.bridgeEchoLine) return fillText(i18n.bridgeEchoLine, { linea: names[0] });
    var score = birthScoreOf(summary);
    if (score != null && i18n.bridgeEchoScore) return fillText(i18n.bridgeEchoScore, { score: score });
    return i18n.bridgeSentence || '';
  }
  function makeEl(doc, tag, className, hook) {
    var el = doc.createElement(tag);
    if (className) el.className = className;
    if (hook) el.setAttribute(hook, '');
    return el;
  }
  function insertAfter(ref, node) { ref.parentNode.insertBefore(node, ref.nextSibling); }
  /* DOM: builds the receipt inside .aw-astro__bridge. Frame, lead, list,
     close line and fine print are created when the markup lacks them (a
     partial section sync or the harness clone degrade, never break).
     Runs in BOTH suspense states and again after every city result; the
     close line always runs, even with an empty list, so "brújula de
     objetivos" is named exactly once per page (the area line owns it when
     painted, ctx.areaLineShown, otherwise this close does). */
  function renderBridgeReceipt(rootEl, i18n, summary, picked, doors, ctx) {
    var bridge = rootEl && rootEl.querySelector ? rootEl.querySelector('.aw-astro__bridge') : null;
    var doc = global.document;
    if (!bridge || !doc || !doc.createElement) return;
    i18n = i18n || {};
    ctx = ctx || sessionFor(rootEl);
    var box = bridge.querySelector('.aw-astro__bridge-card') || bridge;
    var cta = box.querySelector('a[data-aw-event]');
    var frame = bridge.querySelector('[data-aw-astro-bridge-frame]');
    if (!frame) {
      frame = makeEl(doc, 'p', 'aw-astro__bridge-frame', 'data-aw-astro-bridge-frame');
      var ref = bridge.querySelector('.aw-astro__bridge-title') || bridge.querySelector('.aw-astro__bridge-sentence');
      if (ref) insertAfter(ref, frame); else bridge.insertBefore(frame, bridge.firstChild);
    }
    // The scale frame always precedes the numbers; with a null natal score
    // the generic variant speaks of numbers that were never painted.
    var frameTxt = (birthScoreOf(summary) != null ? i18n.bridgeFrame : (i18n.bridgeFrameNoScore || i18n.bridgeFrame)) || '';
    frame.textContent = frameTxt;
    if (frameTxt) frame.removeAttribute('hidden'); else frame.setAttribute('hidden', '');
    var partsOn = hasParts(i18n);
    var textEl = box.querySelector('[data-aw-astro-bridge-text]');
    // La línea de cierre (la corta de i18n) se queda solo en el panel del candado desde el
    // 25-sep (founder): sin las cuatro partes la tarjeta no la pinta ni guarda un párrafo vacío.
    if (!partsOn && textEl && textEl.parentNode) { textEl.parentNode.removeChild(textEl); textEl = null; }
    if (partsOn && !textEl) {
      textEl = makeEl(doc, 'p', '', 'data-aw-astro-bridge-text');
      if (cta) box.insertBefore(textEl, cta); else box.appendChild(textEl);
    }
    var ref = textEl || cta || null;
    var lead = box.querySelector('[data-aw-astro-bridge-open-lead]');
    if (!lead) { lead = makeEl(doc, 'p', 'aw-astro__bridge-open-lead', 'data-aw-astro-bridge-open-lead'); box.insertBefore(lead, ref); }
    var list = box.querySelector('[data-aw-astro-bridge-open]');
    if (!list) { list = makeEl(doc, 'ul', 'aw-astro__bridge-open', 'data-aw-astro-bridge-open'); box.insertBefore(list, ref); }
    var lineNames = (picked || []).map(unitLineName).filter(Boolean);
    var items = buildReceiptItems(summary, lineNames, doors, ctx.searched, i18n);
    list.innerHTML = '';
    items.forEach(function (txt) {
      var li = doc.createElement('li');
      li.textContent = txt;
      list.appendChild(li);
    });
    lead.textContent = i18n.bridgeOpenLead || '';
    if (items.length && lead.textContent) lead.removeAttribute('hidden'); else lead.setAttribute('hidden', '');
    if (items.length) list.removeAttribute('hidden'); else list.setAttribute('hidden', '');
    var sentEl = bridge.querySelector('.aw-astro__bridge-sentence');
    if (sentEl) {
      var echo = pickBridgeEcho(summary, lineNames, ctx.searched, i18n);
      if (echo) sentEl.textContent = echo;
    }
    // Con las cadenas del inventario en i18n la línea de cierre es la corta.
    // Sin ellas manda el interruptor por dueño de la línea de terreno.
    // Bloque conciso (6-sep): with the four part strings the close line is
    // the parts lead and the 11-line inventory folds into a closed details.
    var inventoryOn = hasInventory(i18n);
    if (textEl) {
      // Solo con las cuatro partes: su entrada (parts_lead). Sin parts_lead (18-sep) se oculta.
      var close = i18n.parts_lead || '';
      if (close) textEl.textContent = close;
      if (!close) textEl.setAttribute('hidden', ''); else textEl.removeAttribute('hidden');
      // the parts lead mirrors the receipt lead, so it takes the same chrome
      textEl.classList.add('aw-astro__parts-lead');
    }
    // Same guard as the peak card: when no famous city beats the birth city
    // the first part switches to the variant that does not contradict tile 1.
    var topScore = summary && summary.top_city ? Number(summary.top_city.score) : NaN;
    var birthMax = birthScoreOf(summary) != null && isFinite(topScore) && topScore <= birthScoreOf(summary);
    var partsEl = partsOn ? renderParts(box, textEl, cta, i18n, doc, birthMax) : null;
    // Sin las cuatro partes desde el 25-sep (las sustituye la lista de la tarjeta): el
    // inventario sigue plegado tras «Ver todo lo que trae tu lectura».
    if (inventoryOn) renderInventory(box, partsEl || textEl, cta, i18n, doc, partsOn || !!i18n.inv_more);
    // Sin letra fina en el puente desde el 25-sep (founder).
    if (items.length && !receiptEvtFired) { receiptEvtFired = true; pushEvent('astro_bridge_receipt_shown'); } // structure only
  }
  /* Inventory of the reading: "Todo lo que trae tu
     lectura", 11 lines in 4 groups in the PDF's own order, STRUCTURAL (the
     same for every chart: no personal tokens), painted between the short
     close line and the CTA, once per load, in both suspense states, whether
     or not the natal fetch worked. Pure list: no chip, no bars, no link per
     item. Strings come from i18n only. */
  var INV_GROUPS = [['inv_group_1', ['inv_1', 'inv_2', 'inv_3']], ['inv_group_2', ['inv_4', 'inv_5', 'inv_6']], ['inv_group_3', ['inv_7', 'inv_8', 'inv_9']], ['inv_group_4', ['inv_10', 'inv_11']]];
  function hasInventory(i18n) {
    if (!i18n || !i18n.inv_lead) return false;
    for (var i = 1; i <= 11; i++) { if (!i18n['inv_' + i]) return false; }
    return true;
  }
  /* Four parts of the reading (bloque conciso, 2026-09-06): one catch line
     per part of the PDF, in the PDF's order, STRUCTURAL (no personal
     tokens), painted between the parts lead and the folded inventory. The
     strings come from i18n only. */
  function hasParts(i18n) {
    if (!i18n || !i18n.inv_more) return false; // parts_lead es opcional desde el 18-sep
    for (var i = 1; i <= 4; i++) { if (!i18n['part_' + i + '_title'] || !i18n['part_' + i + '_line']) return false; }
    return true;
  }
  function renderParts(box, textEl, cta, i18n, doc, birthMax) {
    var ul = box.querySelector('[data-aw-astro-parts]');
    if (!ul) {
      ul = makeEl(doc, 'ul', 'aw-astro__parts', 'data-aw-astro-parts');
      if (cta) box.insertBefore(ul, cta); else box.appendChild(ul);
    }
    // keep the order lead → parts → (inventory) → CTA even when the markup shifted
    if (textEl && textEl.parentNode === box && ul.previousElementSibling !== textEl) box.insertBefore(ul, textEl.nextSibling);
    ul.innerHTML = '';
    for (var i = 1; i <= 4; i++) {
      var li = doc.createElement('li');
      li.className = 'aw-astro__part';
      var title = doc.createElement('b');
      title.className = 'aw-astro__part-title';
      title.textContent = i18n['part_' + i + '_title'];
      var line = doc.createElement('span');
      line.className = 'aw-astro__part-line';
      var lineKey = (i === 1 && birthMax && i18n.part_1_line_max) ? 'part_1_line_max' : 'part_' + i + '_line';
      line.textContent = i18n[lineKey];
      li.appendChild(title);
      li.appendChild(line);
      ul.appendChild(li);
    }
    return ul;
  }
  var inventoryEvtFired = false;
  var inventorySeenFired = false;
  var inventoryOpenFired = false;
  /* anchor: the element the inventory block follows (the parts list when
     the parts are on, the close line otherwise). folded: wrap the 11 lines
     in a closed native details whose summary is i18n.inv_more (the lead
     paragraph is dropped: the group labels do its job inside the fold). */
  function renderInventory(box, anchor, cta, i18n, doc, folded) {
    var lead = box.querySelector('[data-aw-astro-inv-lead]');
    var inv = box.querySelector('[data-aw-astro-inventory]');
    var more = box.querySelector('[data-aw-astro-inv-more]');
    if (!inv) inv = makeEl(doc, 'div', 'aw-astro__inventory', 'data-aw-astro-inventory');
    if (folded) {
      if (lead && lead.parentNode) lead.parentNode.removeChild(lead);
      if (!more) {
        more = makeEl(doc, 'details', 'aw-astro__inv-more', 'data-aw-astro-inv-more');
        var sum = doc.createElement('summary');
        sum.className = 'aw-astro__inv-summary';
        more.appendChild(sum);
        more.addEventListener('toggle', function () {
          if (more.open && !inventoryOpenFired) { inventoryOpenFired = true; pushEvent('astro_inventory_open'); } // structure only
        });
      }
      var summary = more.querySelector('summary');
      if (summary) summary.textContent = i18n.inv_more;
      if (inv.parentNode !== more) more.appendChild(inv);
      if (more.parentNode !== box) { if (cta) box.insertBefore(more, cta); else box.appendChild(more); }
      if (anchor && anchor.parentNode === box && more.previousElementSibling !== anchor) box.insertBefore(more, anchor.nextSibling);
    } else {
      if (more && more.parentNode) { if (inv.parentNode === more) box.insertBefore(inv, more); more.parentNode.removeChild(more); }
      if (!lead) {
        lead = makeEl(doc, 'p', 'aw-astro__inv-lead', 'data-aw-astro-inv-lead');
        if (cta) box.insertBefore(lead, cta); else box.appendChild(lead);
      }
      if (inv.parentNode !== box) { if (cta) box.insertBefore(inv, cta); else box.appendChild(inv); }
      // keep the order close → lead → inventory → CTA even when the markup shifted
      if (anchor && anchor.parentNode === box && lead.previousElementSibling !== anchor) box.insertBefore(lead, anchor.nextSibling);
      if (lead.nextSibling !== inv) box.insertBefore(inv, lead.nextSibling);
      lead.textContent = i18n.inv_lead;
    }
    inv.innerHTML = '';
    INV_GROUPS.forEach(function (g) {
      var label = doc.createElement('p');
      label.className = 'aw-astro__inv-group';
      label.textContent = i18n[g[0]] || '';
      if (!label.textContent) label.setAttribute('hidden', '');
      inv.appendChild(label);
      var ul = doc.createElement('ul');
      ul.className = 'aw-astro__inv-list';
      g[1].forEach(function (k) {
        var li = doc.createElement('li');
        li.textContent = i18n[k];
        ul.appendChild(li);
      });
      inv.appendChild(ul);
    });
    if (!inventoryEvtFired) { inventoryEvtFired = true; pushEvent('astro_inventory_shown'); } // structure only
    if (!inventorySeenFired && typeof global.IntersectionObserver === 'function') {
      try {
        var io = new global.IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              if (!inventorySeenFired) { inventorySeenFired = true; pushEvent('astro_inventory_seen'); }
              io.disconnect();
              return;
            }
          }
        }, { threshold: 0.12 });
        io.observe(folded && more ? more : inv); // folded: the summary row is what she sees
      } catch (e) {}
    }
  }
  /* New chart = new session: a recalculate never inherits the previous
     chart's receipt, echo or area line (called from render() and from
     aw-astromapa.js's clearTeaserZone). */
  function resetSession(rootEl, i18n) {
    var s = sessionFor(rootEl);
    s.searched = [];
    s.areaLineShown = false;
    s.brujulaShown = false;
    s.receipt = { summary: null, picked: [], doors: [] };
    if (!rootEl || !rootEl.querySelector) return;
    areaSlot(rootEl, 'release'); // a slot reserved mid-search never survives a new chart
    ['[data-aw-astro-area-line]', '[data-aw-astro-cap-list]', '[data-aw-astro-cap-jump]', '[data-aw-astro-bridge-frame]', '[data-aw-astro-bridge-open-lead]', '[data-aw-astro-bridge-open]'].forEach(function (sel) {
      var el = rootEl.querySelector(sel);
      if (el) el.setAttribute('hidden', '');
    });
    var sentEl = rootEl.querySelector('.aw-astro__bridge-sentence');
    if (sentEl && i18n && i18n.bridgeSentence) sentEl.textContent = i18n.bridgeSentence;
    var textEl = rootEl.querySelector('[data-aw-astro-bridge-text]');
    // Sin las cuatro partes la tarjeta no lleva línea de cierre (25-sep): nada que restaurar.
    var resetClose = i18n && hasParts(i18n) ? (i18n.parts_lead || '') : '';
    if (textEl && resetClose) textEl.textContent = resetClose;
    var more = rootEl.querySelector('[data-aw-astro-inv-more]');
    if (more) more.open = false; // a new chart starts with the index folded again
    // tile labels back to the stored generic text until the next chart names them
    applyStatLabel(rootEl.querySelector('[data-aw-astro-stat="birth_score"]'), null, '');
  }

  /* Natal city name in the tiles and the base anchor (P13, 2026-09-05).
     The name is the autocomplete pick (i18n.cityName, client-side, tildes
     intact), never the API echo and never analytics. A tile label competes
     with its numeral at 390 px, so names over 16 characters keep the stored
     generic label (Ciudad de México, 16, fits). The library anchors are
     untouched: they carry the astrologer gate and inherit the referent. */
  var STAT_LABEL_MAX = 16;
  function statLabelFor(tpl, city, fallback) {
    var c = city != null ? String(city).trim() : '';
    if (c && c.length <= STAT_LABEL_MAX && tpl) return fillText(tpl, { ciudad: c });
    return fallback != null ? fallback : '';
  }
  function baseAnchorHtml(i18n, score) {
    i18n = i18n || {};
    var c = i18n.cityName != null ? String(i18n.cityName).trim() : '';
    if (c && i18n.baseAnchorCityTpl) return fillTpl(i18n.baseAnchorCityTpl, { ciudad: c, score: score });
    return i18n.baseAnchorTpl ? fillTpl(i18n.baseAnchorTpl, { score: score }) : '';
  }
  // Rewrites the tile label next to a stat numeral; the stored generic label
  // is stashed on first touch so a later chart (or a reset) can restore it.
  function applyStatLabel(numEl, tpl, city) {
    var wrap = numEl && numEl.parentNode;
    var label = wrap && wrap.querySelector ? wrap.querySelector('.aw-astro__stat-label') : null;
    if (!label) return;
    if (!label.hasAttribute('data-aw-astro-label-default')) label.setAttribute('data-aw-astro-label-default', label.textContent);
    label.textContent = statLabelFor(tpl, city, label.getAttribute('data-aw-astro-label-default'));
  }

  /* render(summary, lib, rootEl, i18n) — fills the markup shipped in
     sections/aw-astromapa.liquid's [data-aw-astro-interp] zone and
     unhides it. Every summary field access is guarded: a missing or
     malformed field degrades that piece of UI, it never throws. */
  var lastLib = null; // stashed for the per-search readable unit (E25)

  function render(summary, lib, rootEl, i18n) {
    if (!rootEl || !summary) return;
    i18n = i18n || {};
    setUnits(rootEl);
    lastLib = lib || lastLib;
    resetSession(rootEl, i18n); // a painted summary opens a new session
    var sus = suspenseOn(rootEl);
    var interpElEarly = rootEl.querySelector('[data-aw-astro-interp]');
    if (sus && interpElEarly) interpElEarly.classList.add('is-suspense');

    var birthCity = summary.birth_city || {};
    // 0 is a legitimate score (every pool minimum maps to it) and renders
    // honestly; the API sends null when the computation failed, so null is
    // the only suppress case.
    var birthScoreOk = birthCity.score != null && isFinite(Number(birthCity.score)) && Number(birthCity.score) >= 0;
    var scoreEl = rootEl.querySelector('[data-aw-astro-stat="birth_score"]');
    if (scoreEl) scoreEl.textContent = birthScoreOk ? String(birthCity.score) : '';
    // Both tile labels share one referent (the chart's city), score or no
    // score: with a null score the numeral is empty, the label still names
    // the same place tile 2 names, never a mixed row.
    applyStatLabel(scoreEl, i18n.statBirthLabelTpl, i18n.cityName);
    var barEl = rootEl.querySelector('[data-aw-astro-bar="birth"]');
    if (barEl) barEl.style.width = (birthScoreOk ? Math.max(0, Math.min(100, Number(birthCity.score))) : 0) + '%';

    // La baldosa de líneas cercanas se retiró el 18-sep (founder): la puntuación
    // natal ocupa sola la fila, y las distancias las cuentan las unidades.

    var topCity = summary.top_city || {};
    var topEl = rootEl.querySelector('[data-aw-astro-stat="top_city"]');
    if (topEl) {
      topEl.innerHTML = topCity.name != null && topCity.score != null && isFinite(Number(topCity.score))
        ? esc(T(topCity.name)) + ' · ' + esc(String(topCity.score))
        : '';
    }

    var picked = selectAstroUnits(summary, lib);
    var html = '';
    // La zona abre con la unidad base (la tarjeta temprana TU INFORME del
    // 3-sep se retiró el 5-sep: 1 clic en 145 sesiones y partía en dos la
    // única acción primaria; el puente final es la única fuente de
    // bridge_click_lectura_astro). Base unit always leads: 100 % of charts
    // get ≥1 readable unit, so doors always have at least one key beside them.
    var baseUnit = pickBaseUnit(summary, lib);
    if (baseUnit) {
      var baseAnchor = baseAnchorHtml(i18n, baseUnit.score); // city-named when the pick is known (P13)
      // optional base titular (library v2 stretch lot): titular leads, the score sentence demotes to the fact line
      var baseTitular = lib && lib.base && lib.base[baseUnit.key] && lib.base[baseUnit.key].titular ? String(lib.base[baseUnit.key].titular).trim() : '';
      // Insight first (2026-10-12): with a titular, the score line follows the body.
      html += '<article class="aw-astro__unit aw-astro__unit--base">' +
        (baseTitular
          ? '<h3 class="aw-astro__unit-titular">' + esc(baseTitular) + '</h3>' +
            '<p class="aw-astro__unit-body">' + esc(baseUnit.body) + '</p>' +
            (baseAnchor ? '<p class="aw-astro__unit-fact aw-astro__unit-anchor">' + baseAnchor + '</p>' : '')
          : (baseAnchor ? '<h3 class="aw-astro__unit-anchor">' + baseAnchor + '</h3>' : '') +
            '<p class="aw-astro__unit-body">' + esc(baseUnit.body) + '</p>') +
        '</article>';
      if (!picked.length) pushEvent('astro_fallback_unit_shown'); // UI state, no chart data
    }
    var gSeen = {}; // planets and angles already explained in this result (aw-glosa.js)
    picked.forEach(function (u) { html += readableUnitHtml(u, gSeen); });

    var readableCount = picked.length + (baseUnit ? 1 : 0);
    var topHtml = sus ? topCardHtml(summary, i18n) : '';
    var brujOk = brujulaOkFor(i18n, picked, summary);
    var doorsAll = selectAstroDoors(summary, readableCount, brujOk, topHtml !== '');
    var doors = doorsAll;

    // Orden D6 revisado (7-sep): legibles → zone → carta del pico (regalo, sin
    // candado, y unico separador) → brujula. Dos candados nunca son hermanos.
    var lockLine = i18n.lockLine || '';
    var lockLink = own(LOCK_LINK, lang()) || '/pages/lectura-astrocartografia';
    if (doors.indexOf('zone') > -1) {
      html += lockedUnitHtml(
        fillTpl(i18n.lockedZoneTpl, { continente: T(summary.challenge_zone.continent_es) }),
        { chip: i18n.lockChip, sectionLine: i18n.zoneSectionLine, lockLine: lockLine, link: lockLink, key: 'zone' }
      );
    }
    html += topHtml;
    if (doors.indexOf('brujula') > -1) {
      var brujRows = brujulaRows(i18n, picked, summary);
      html += lockedUnitHtml(
        brujulaAnchor(summary, i18n, topHtml !== ''),
        { chip: i18n.lockChip, handoff: i18n.brujulaSectionLine, lockLine: i18n.brujulaLockLine || lockLine, link: lockLink, key: 'brujula',
          rows: brujRows }
      );
      // Solo estructura (zona por terreno pintada o no), cero datos de carta:
      // el ratio clic/pintado del candado es la lectura pre-registrada.
      pushEvent(brujRows.length && brujRows[0].zona ? 'astro_brujula_zonas' : 'astro_brujula_generica');
      // Propiedad de la frase: con el candado pintado, la linea de area de una
      // ciudad buscada pasa a su variante corta y no repite la brujula.
      sessionFor(rootEl).brujulaShown = true;
    } else if (!brujOk) {
      pushEvent('astro_brujula_suprimida'); // solo estructura, cero datos de carta
    }
    // Regression sentinel for guardrail G5 — with selectAstroDoors in
    // place this must be unreachable (structure-only event, no chart data).
    if (doors.length > picked.length + (baseUnit ? 1 : 0)) pushEvent('astro_doors_overflow'); // base unit counts as readable (G5)

    var unitsEl = rootEl.querySelector('[data-aw-astro-units]');
    if (unitsEl) unitsEl.innerHTML = html;
    // Compra en el candado (11-sep): con la palanca, cada candado se anuncia como
    // desplegable. Sin ella el marcado es el de siempre.
    if (unitsEl && rootEl.getAttribute('data-aw-astro-inline-buy') === '1' && buyReady(i18n)) {
      Array.prototype.forEach.call(unitsEl.querySelectorAll('[data-aw-handoff-key]'), function (l) { l.setAttribute('aria-expanded', 'false'); });
    }
    // Handoff writer: on lock click, leave the approved sectionLine in
    // sessionStorage so the sale landing reopens where the lock closed.
    // Delegated once per container (innerHTML re-renders survive it);
    // sessionStorage can throw (private mode) and the click must never break.
    // The slot F lock (natal v2) lives in this container too: its hook rides
    // only when the attribute exists (undefined is dropped by JSON.stringify,
    // so the gap/zone payloads keep their exact shape).
    // Compra en el candado (11-sep): el clic normal abre el panel y el payload lo deja
    // «Ver todo lo que trae tu lectura», así una llegada posterior por el puente no
    // hereda la franja (consumo único del ADD4). El clic con modificador (pestaña
    // nueva) y la palanca apagada siguen exactamente como antes.
    if (unitsEl && !unitsEl.getAttribute('data-aw-handoff-wired')) {
      unitsEl.setAttribute('data-aw-handoff-wired', '1');
      unitsEl.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target : null;
        if (!t) return;
        var more = t.closest('[data-aw-astro-buy-more]');
        if (more) { writeHandoff(unitsEl.querySelector('[data-aw-handoff-key="' + more.getAttribute('data-aw-astro-buy-more') + '"]')); return; }
        if (t.closest('[data-aw-astro-buy-close]')) { closeBuyPanel(unitsEl, true); return; }
        var a = t.closest('[data-aw-handoff-key]');
        if (!a) return;
        var inline = rootEl.getAttribute('data-aw-astro-inline-buy') === '1' && buyReady(i18n) &&
          !(e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
        if (inline && a.getAttribute('aria-expanded') === 'true') { e.preventDefault(); closeBuyPanel(unitsEl, false); return; }
        if (!inline) writeHandoff(a);
        // Al píxel de Meta: solo la clave del candado (gap/zone), nunca copy.
        try { if (typeof global.awPublish === 'function') global.awPublish('candado_click', { calc: 'astro', key: a.getAttribute('data-aw-handoff-key') || '' }); } catch (err) {}
        if (inline) { e.preventDefault(); openBuyPanel(rootEl, unitsEl, a, i18n); }
      });
      unitsEl.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && unitsEl.querySelector('[data-aw-astro-buy]')) closeBuyPanel(unitsEl, true);
      });
      // El form del panel es el /cart/add de la landing: aw-cart-add.js lo intercepta
      // en captura y sigue a /cart. Aquí solo la señal, con la clave del candado.
      unitsEl.addEventListener('submit', function (e) {
        var f = e.target && e.target.closest ? e.target.closest('[data-aw-astro-buy-form]') : null;
        var p = f ? f.closest('[data-aw-astro-buy]') : null;
        if (p) pushEvent('astro_candado_atc', { lock_key: p.getAttribute('data-aw-astro-buy') || '' });
      });
    }
    // El puente (camino que sí convierte) también viaja al píxel de Meta.
    var bridgeEl = rootEl.querySelector('.aw-astro__bridge');
    if (bridgeEl && !bridgeEl.getAttribute('data-aw-bridge-wired')) {
      bridgeEl.setAttribute('data-aw-bridge-wired', '1');
      bridgeEl.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[data-aw-event]') : null;
        if (!a) return;
        try { if (typeof global.awPublish === 'function') global.awPublish('puente_click', { calc: 'astro' }); } catch (err) {}
      });
    }

    var interpEl = rootEl.querySelector('[data-aw-astro-interp]');
    if (interpEl) interpEl.removeAttribute('hidden');

    if (sus) {
      personalizeBridge(rootEl, i18n);
      installSeenObservers(rootEl, interpEl);
      if (!prefersReduced() && !isLite() && interpEl) runCascade(rootEl, interpEl);
    }
    // Session receipt on the final bridge, in both suspense states, after
    // the (suspense-only) title personalization. The receipt keeps the
    // legacy doors (its list is the pre-registered P1 surface): what this
    // page leaves open does not change because the zone door yields to slot F.
    var session = sessionFor(rootEl);
    var receiptDoors = doorsAll;
    session.receipt = { summary: summary, picked: picked, doors: receiptDoors };
    renderBridgeReceipt(rootEl, i18n, summary, picked, receiptDoors, session);

    pushEvent('astro_teaser_shown'); // structure only, never which units fired
  }

  /* Personalized bridge (suspense only): title from the CHOSEN autocomplete
     city (i18n.cityName, set client-side, correct accents, never analytics). */
  function personalizeBridge(rootEl, i18n) {
    var city = i18n.cityName;
    if (!city) return;
    var titleEl = rootEl.querySelector('.aw-astro__bridge-title');
    if (titleEl && i18n.bridgeTitleTpl) titleEl.textContent = String(i18n.bridgeTitleTpl).replace(/\{ciudad\}/g, city);
    var sentEl = rootEl.querySelector('.aw-astro__bridge-sentence');
    if (sentEl && i18n.bridgeSentence) sentEl.textContent = i18n.bridgeSentence;
  }

  /* One-shot visibility events, SEPARATE from the reveal observer so the
     deadline can never contaminate "seen" (sinastría D10 lesson). Session-
     deduped; structure only. */
  var seenFired = {};
  function installSeenObservers(rootEl, interpEl) {
    if (typeof global.IntersectionObserver !== 'function' || !interpEl) return;
    // astro_locks_seen: la primera unidad bloqueada de la zona de unidades.
    var pairs = [
      [interpEl.querySelector('[data-aw-astro-stats]'), 'astro_teaser_seen'],
      [interpEl.querySelector('[data-aw-astro-units] .aw-astro__unit--locked'), 'astro_locks_seen'],
      [interpEl.querySelector('[data-aw-astro-topcard]'), 'astro_top_city_seen'],
      [rootEl.querySelector('.aw-astro__bridge'), 'astro_bridge_seen']
    ];
    // Idempotente por evento (seenFired) y por elemento (_awSeenIo).
    pairs.forEach(function (pair) {
      var el = pair[0], evt = pair[1];
      if (!el || seenFired[evt] || el._awSeenIo) return;
      var io = new global.IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            if (!seenFired[evt]) { seenFired[evt] = true; pushEvent(evt); }
            io.disconnect();
            el._awSeenIo = null;
            return;
          }
        }
      }, { threshold: 0.12 });
      el._awSeenIo = io;
      io.observe(el);
    });
  }

  /* Progressive reveal of the interpretation zone (suspense, motion-capable
     only). Four guards: (1) staging class added here, JS-dead = all visible;
     (2) observer installs at cascade end or first scroll; (3) reduced-motion
     never reaches this function; (4) deadline anchored to visibility:
     min(first intersection + 2.5 s, mount + 15 s) reveals everything. */
  function runCascade(rootEl, interpEl) {
    var doc = global.document;
    if (!doc) return;
    var title = interpEl.querySelector('.aw-astro__interp-title');
    var stats = interpEl.querySelectorAll('.aw-astro__stat');
    var note = interpEl.querySelector('.aw-astro__score-note');
    var scoreEl = interpEl.querySelector('[data-aw-astro-stat="birth_score"]');
    var barEl = interpEl.querySelector('[data-aw-astro-bar="birth"]');
    var below = [];
    Array.prototype.forEach.call(interpEl.querySelectorAll('[data-aw-astro-units] > *'), function (el) { below.push(el); });
    var citycard = interpEl.querySelector('[data-aw-astro-citycard]');
    if (citycard) below.push(citycard);
    var bridge = rootEl.querySelector('.aw-astro__bridge');
    if (bridge) below.push(bridge);

    var timed = [title, stats[0] || null, note];
    var all = [];
    timed.forEach(function (el) { if (el) { el.classList.add('aw-astro__cascade'); all.push(el); } });
    below.forEach(function (el) {
      el.classList.add('aw-astro__cascade', 'aw-astro__cascade--up');
      all.push(el);
    });
    rootEl.classList.add('is-astro-staged');

    var timers = [];
    var revealed = false;
    var io = null;
    function revealAllNow() {
      if (revealed) return;
      revealed = true;
      timers.forEach(clearTimeout);
      if (io) { try { io.disconnect(); } catch (e) {} }
      rootEl.classList.remove('is-astro-staged');
      all.forEach(function (el) { el.classList.remove('aw-astro__cascade', 'aw-astro__cascade--up', 'is-in', 'is-in-up'); el.style.transitionDelay = ''; });
      if (barEl) { barEl.classList.remove('is-scale'); barEl.classList.add('is-grown'); }
    }
    // Incondicional safety net: sequence retention must never depend on an
    // interaction that may not happen.
    timers.push(setTimeout(revealAllNow, 15000));

    timers.push(setTimeout(function () { if (title) title.classList.add('is-in'); }, 0));
    timers.push(setTimeout(function () {
      if (stats[0]) stats[0].classList.add('is-in');
      if (scoreEl && scoreEl.textContent) {
        var finalScore = parseInt(scoreEl.textContent, 10);
        if (isFinite(finalScore)) countUpEl(scoreEl, finalScore, 550);
      }
      if (barEl) growBar(barEl);
    }, 150));
    timers.push(setTimeout(function () { if (note) note.classList.add('is-in'); }, 700));

    var installed = false;
    function installObserver() {
      if (installed || revealed) return;
      installed = true;
      if (typeof global.IntersectionObserver !== 'function') { revealAllNow(); return; }
      var sawFirst = false;
      io = new global.IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var el = en.target;
          if (!sawFirst) {
            sawFirst = true;
            timers.push(setTimeout(revealAllNow, 2500)); // visibility-anchored deadline
          }
          // Doors arrive 120 ms later than the rest: they are the doors.
          if (el.classList.contains('aw-astro__unit--locked')) el.style.transitionDelay = '120ms';
          el.classList.add('is-in-up');
          io.unobserve(el);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -12% 0px' });
      below.forEach(function (el) { io.observe(el); });
    }
    timers.push(setTimeout(installObserver, 900));
    try {
      doc.addEventListener('scroll', function onScroll() {
        doc.removeEventListener('scroll', onScroll);
        installObserver();
      }, { passive: true, once: true });
    } catch (e) {}
  }

  /* ── city-search submodule (3-per-session cap) ──────────────── */
  var CITY_CAP_KEY = 'aw_astro_city_searches';
  var CITY_CAP_MAX = 3;
  var CAP_EVT_KEY = 'aw_astro_cap_evt';
  var SIN_LINEA_FALLBACK = T('Sin líneas de conjunción a menos de 1.100 km');

  // Fires astro_search_capped at most once per session. Same tolerance
  // model as citySearchState below: a storage read/write that throws
  // (private-mode Safari etc.) never crashes, it just skips the dedupe.
  function pushCapEventOnce(storage) {
    try { if (storage.getItem(CAP_EVT_KEY)) return; } catch (e) {}
    try { storage.setItem(CAP_EVT_KEY, '1'); } catch (e) {}
    pushEvent('astro_search_capped');
  }

  /* Pure — no DOM. Wraps a Storage-like object (getItem/setItem).
     Tolerant of a missing/corrupt stored value and of the storage
     calls themselves throwing (sessionStorage can throw in private
     mode): either case is treated as "0 used", never crashes. */
  function citySearchState(storage) {
    function readUsed() {
      try {
        var n = parseInt(storage.getItem(CITY_CAP_KEY), 10);
        return isFinite(n) && n > 0 ? n : 0;
      } catch (e) { return 0; }
    }
    function writeUsed(n) {
      try { storage.setItem(CITY_CAP_KEY, String(n)); } catch (e) {}
    }
    return {
      remaining: function () { return Math.max(0, CITY_CAP_MAX - readUsed()); },
      record: function () { writeUsed(readUsed() + 1); },
      capped: function () { return readUsed() >= CITY_CAP_MAX; }
    };
  }

  // A search result must never name a line the map cannot show (visible
  // self-contradiction on one screen). Chiron is drawn since 2026-08-29
  // (API commit 2e147fc); nodes and Lilith stay undrawn.
  var NON_DRAWN_BODIES = { nodo_norte: 1, nodo_sur: 1, lilith: 1 };

  /* Returns the dominant-line sentence, or null (no line: i18n sinLinea
     copy), or '' (line exists but its body is not drawn on the map:
     omit the sentence entirely rather than name it or falsely deny it). */
  function lineFactText(line) {
    if (!line || !line.planet_es || !line.angle || line.distance_km == null) return null;
    if (NON_DRAWN_BODIES[slug(line.planet_es)]) return '';
    return fillText(T('Tu línea {planeta} {angulo} pasa a {km} km'), { planeta: T(line.planet_es), angulo: angleLabel(line.angle), km: kmText(line.distance_km, line.angle) });
  }

  /* Area line under the city rows (P4, 2026-09-05). The brújula de objetivos
     groups the report by the line's planet (fact sheet: conexiones profundas
     Venus, recursos Júpiter, oportunidades profesionales Sol, refugios de
     bienestar Luna). Planet variant ONLY when the dominant line is one of
     those four, drawn on the map AND inside the 1.100 km radius (astrologer
     blocker, canon §17: the selection is itself the relevance claim). Every
     other case (no line, far line, undrawn body, other planet, score 0)
     takes the generic variant, which depends on nothing but the city name.
     Pure: no DOM. Never says the number is the report's global column,
     never "sirve para", never that the report analyses the searched city. */
  var AREA_BY_PLANET = { venus: 'areaVenus', jupiter: 'areaJupiter', sol: 'areaSol', luna: 'areaLuna' };
  function pickAreaLine(cityName, data, i18n, brujulaOwned) {
    i18n = i18n || {};
    var dl = data && data.dominant_line;
    var score = data && data.score;
    var near = !!(dl && dl.planet_es && dl.distance_km != null && isFinite(Number(dl.distance_km)) &&
      Number(dl.distance_km) <= 1100 && !NON_DRAWN_BODIES[slug(dl.planet_es)] &&
      !(score != null && Number(score) === 0));
    var areaKey = near ? AREA_BY_PLANET[slug(dl.planet_es)] : null;
    var area = areaKey ? i18n[areaKey] : '';
    // Con el candado de la brujula pintado, la frase «brujula de objetivos» ya
    // tiene dueno en la pagina: aqui se usa la variante corta, que no la repite.
    var planetTpl = (brujulaOwned && i18n.areaLinePlanetShortTpl) ? i18n.areaLinePlanetShortTpl : i18n.areaLinePlanetTpl;
    var genericTpl = (brujulaOwned && i18n.areaLineGenericShortTpl) ? i18n.areaLineGenericShortTpl : i18n.areaLineGenericTpl;
    if (area && planetTpl) return fillText(planetTpl, { ciudad: cityName, planeta: T(dl.planet_es), area: area });
    return genericTpl ? fillText(genericTpl, { ciudad: cityName }) : '';
  }
  var areaEvtFired = false;

  /* Slot reservation for the area line (first search of a chart only):
     the node takes its height, invisible, the moment the skeleton row
     appears, so the text fill ~2,5 s later (outside the 500 ms input
     window) does not push the cap note and the bridge down. 'fill' keeps
     the node and drops the modifier; 'release' (error path, reset) hides
     a node that was reserved and never filled. Null-guarded. */
  var AREA_SKEL = 'aw-astro__area-line--skel';
  function areaSlot(rootEl, action) {
    var el = rootEl && rootEl.querySelector ? rootEl.querySelector('[data-aw-astro-area-line]') : null;
    if (!el || !el.classList) return;
    if (action === 'reserve') {
      if (!el.hasAttribute('hidden')) return; // already painted: a later search only rewrites the text
      el.textContent = '';
      el.classList.add(AREA_SKEL);
      el.removeAttribute('hidden');
    } else if (action === 'fill') {
      el.classList.remove(AREA_SKEL);
    } else if (el.classList.contains(AREA_SKEL)) {
      el.classList.remove(AREA_SKEL);
      el.setAttribute('hidden', '');
    }
  }

  /* Cap recap (P7, 2026-09-05): the searched cities of THIS load by NAME,
     in typing order, never their scores (each search is normalized with its
     own city inside the pool, so the numbers share no scale and a single
     line of three would fabricate a ranking). '' when nothing was searched
     in this load (cap inherited from sessionStorage after a restore). Pure. */
  function capRecapText(searched, i18n) {
    i18n = i18n || {};
    var names = (searched || []).filter(Boolean).slice(0, CITY_CAP_MAX).map(function (n) {
      return i18n.capItemTpl ? fillText(i18n.capItemTpl, { ciudad: n }) : String(n);
    });
    if (!names.length || !i18n.capRecapTpl) return '';
    return fillText(i18n.capRecapTpl, { lista: joinList(names, i18n.capListSep, i18n.capListLast) });
  }

  /* initCitySearch(rootEl, birthPayload, endpoint, i18n) — wires the
     city-search card shipped in sections/aw-astromapa.liquid
     ([data-aw-astro-citycard]) to the section's places-autocomplete
     proxy (same pattern as aw-astromapa.js's birth-place input) and
     to the basic-summary endpoint, capped at CITY_CAP_MAX searches
     per browser session via sessionStorage. Browser-only: never called
     under Node.

     IDEMPOTENT: aw-astromapa.js calls this again on every successful
     calculate (including a recalculate after "back"), each time with a
     fresh birthPayload/endpoint. cityInput/suggestEl are never re-created
     (only cleared/re-hidden), so re-running the wiring below would stack
     a second, third, ... set of event listeners on the SAME elements —
     every keystroke or search would then fire once per accumulated
     listener set. Instead: `ctx` is a single mutable object stashed on
     the cityInput element (cityInput._awCitySearchCtx) that the ONE
     wired listener set (built only the first time) reads
     birthPayload/endpoint/i18n from. A later call just refreshes ctx's
     fields and re-runs the capped-on-init check, then returns before
     touching any listeners. */
  function initCitySearch(rootEl, birthPayload, endpoint, i18n) {
    if (!rootEl || !birthPayload || !endpoint) return;
    i18n = i18n || {};
    setUnits(rootEl);

    var cityInput = rootEl.querySelector('[data-aw-astro-city-input]');
    var suggestEl = rootEl.querySelector('[data-aw-astro-city-suggest]');
    var resultsEl = rootEl.querySelector('[data-aw-astro-city-results]');
    var capEl = rootEl.querySelector('[data-aw-astro-cap]');
    var capLeftEl = rootEl.querySelector('[data-aw-astro-cap-left]');
    if (!cityInput || !suggestEl || !resultsEl) return;
    var searchWrap = cityInput.closest ? cityInput.closest('.aw-astro__search-wrap') : null;

    var placesProxy = (rootEl.getAttribute('data-places-proxy') || '').replace(/\/+$/, '');
    // sesión de Google y memoria de respuestas (aw-places.js, 2026-10-01)
    var places = (typeof AWPlaces !== 'undefined' && placesProxy) ? AWPlaces.client(placesProxy) : null;
    var state = citySearchState(global.sessionStorage);

    function updateCapLeft() {
      if (!capLeftEl) return;
      var left = state.remaining();
      if (left >= CITY_CAP_MAX || left <= 0) { capLeftEl.setAttribute('hidden', ''); return; }
      var tpl = (ctx && ctx.i18n && ctx.i18n.capLeftTpl) || (i18n && i18n.capLeftTpl) || '';
      if (!tpl) { capLeftEl.setAttribute('hidden', ''); return; }
      capLeftEl.textContent = tpl.replace(/\{n\}/g, String(left));
      capLeftEl.removeAttribute('hidden');
    }

    function applyCapIfNeeded() {
      if (!state.capped()) return false;
      if (searchWrap) searchWrap.hidden = true;
      if (capLeftEl) capLeftEl.setAttribute('hidden', '');
      if (capEl) capEl.removeAttribute('hidden');
      // Cap as a route, not a wall (P7): her cities by name, then the pill
      // that jumps to the bridge receipt. Null-guarded (partial sync / stub).
      var capListEl = rootEl.querySelector('[data-aw-astro-cap-list]');
      if (capListEl) {
        var recap = capRecapText(sessionFor(rootEl).searched, ctx && ctx.i18n);
        capListEl.textContent = recap;
        if (recap) capListEl.removeAttribute('hidden'); else capListEl.setAttribute('hidden', '');
      }
      var jumpEl = rootEl.querySelector('[data-aw-astro-cap-jump]');
      if (jumpEl) {
        if (ctx && ctx.i18n && ctx.i18n.capJumpLabel) jumpEl.textContent = ctx.i18n.capJumpLabel;
        jumpEl.removeAttribute('hidden');
        if (!jumpEl.getAttribute('data-aw-cap-jump-wired')) {
          jumpEl.setAttribute('data-aw-cap-jump-wired', '1');
          jumpEl.addEventListener('click', function (e) {
            pushEvent('astro_cap_jump_click'); // structure only
            var href = jumpEl.getAttribute('href') || '';
            var target = href.charAt(0) === '#' && global.document ? global.document.getElementById(href.slice(1)) : null;
            if (!target) return; // no target: let the anchor behave natively
            if (e && e.preventDefault) e.preventDefault();
            try { target.scrollIntoView({ block: 'start', behavior: prefersReduced() ? 'auto' : 'smooth' }); } catch (err) { try { target.scrollIntoView(); } catch (err2) {} }
            try {
              if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
              target.focus({ preventScroll: true });
            } catch (err) {}
          });
        }
      }
      pushCapEventOnce(global.sessionStorage);
      return true;
    }

    // Re-init: elements were already wired by an earlier call. Refresh the
    // shared ctx (so future searches use the new chart's birth data) and
    // re-apply the cap gate (a recalculate un-hides the search box; if the
    // session was already capped it must go straight back to hidden).
    var ctx = cityInput._awCitySearchCtx;
    if (ctx) {
      ctx.birthPayload = birthPayload;
      ctx.endpoint = endpoint;
      ctx.i18n = i18n;
      if (!applyCapIfNeeded()) updateCapLeft();
      return;
    }
    ctx = { birthPayload: birthPayload, endpoint: endpoint, i18n: i18n };
    cityInput._awCitySearchCtx = ctx;
    cityInput.setAttribute('data-aw-wired', '1');

    if (applyCapIfNeeded()) return; // already capped from an earlier search this session
    updateCapLeft();

    var acItems = [];
    var acActive = -1;
    var acTimer = null;
    var busy = false;

    function clearSuggest() {
      acItems = [];
      acActive = -1;
      suggestEl.innerHTML = '';
      suggestEl.hidden = true;
      cityInput.setAttribute('aria-expanded', 'false');
      cityInput.removeAttribute('aria-activedescendant');
    }
    // Plain li[role=option] rows (a button inside an option is invalid ARIA);
    // the old class name stays so the existing row styles keep applying.
    function renderSuggest(items) {
      acItems = items || [];
      acActive = -1;
      if (!acItems.length) { clearSuggest(); return; }
      suggestEl.innerHTML = acItems.map(function (s, i) {
        return '<li class="aw-astro__suggest-item" role="option" id="' + esc(suggestEl.id + '-opt-' + i) + '" data-i="' + i + '">' +
          '<span class="aw-astro__suggest-btn">' +
          '<span class="aw-astro__c-name">' + esc(s.name) + '</span>' +
          (s.region ? '<span class="aw-astro__c-region">' + esc(s.region) + '</span>' : '') +
          '</span></li>';
      }).join('');
      suggestEl.hidden = false;
      cityInput.setAttribute('aria-expanded', 'true');
    }
    function moveActive(delta) {
      if (!acItems.length || suggestEl.hidden) return;
      acActive = (acActive + delta + acItems.length) % acItems.length;
      Array.prototype.forEach.call(suggestEl.children, function (li, i) {
        li.classList.toggle('is-active', i === acActive);
      });
      cityInput.setAttribute('aria-activedescendant', suggestEl.id + '-opt-' + acActive);
    }
    function fetchAutocomplete(q) {
      if (!places) return;
      places.suggest(q)
        .then(function (d) {
          var arr = (d && d.predictions) || [];
          var items = arr.slice(0, 5).map(function (p) {
            return {
              text: p.description || p.main_text || '',
              name: p.main_text || p.description || '',
              region: p.secondary_text || '',
              id: p.place_id || ''
            };
          }).filter(function (x) { return x.text; });
          if (cityInput.value.trim().length >= 3) renderSuggest(items); // ignore stale
        })
        .catch(function () { renderSuggest([]); });
    }

    function errorMsg() {
      return (ctx.i18n && (ctx.i18n.cityError || ctx.i18n.errNetwork)) || T('No hemos podido puntuar esta ciudad. Vuelve a intentarlo.');
    }
    // Generic retry-able error row: onRetry is called (and the row removed)
    // when the user clicks the retry link. Used for both a failed score
    // fetch (retry = re-run searchCity) and a failed /details lookup
    // (retry = re-run pickSuggestion, since there's no lat/lon yet to search).
    function renderErrorRow(onRetry) {
      areaSlot(rootEl, 'release'); // a reserved, never filled slot goes back to hidden
      var row = document.createElement('div');
      row.className = 'aw-astro__city-error';
      row.setAttribute('role', 'alert');
      row.innerHTML = '<p>' + esc(errorMsg()) + '</p>' +
        '<button type="button" class="aw-astro__retry-link" data-aw-astro-city-retry>' +
        esc((ctx.i18n && ctx.i18n.cityRetryLabel) || T('Reintentar')) + '</button>';
      resultsEl.appendChild(row);
      var btn = row.querySelector('[data-aw-astro-city-retry]');
      if (btn) btn.addEventListener('click', function () {
        if (row.parentNode) row.parentNode.removeChild(row);
        onRetry();
      });
    }
    function renderCityResult(city, data, skelRow) {
      data = data || {};
      var score = data.score;
      // A 200 response with a non-numeric score is a contract violation,
      // not a value to render: show the same retry-able error row rather
      // than a broken "NaN" stat.
      if (score != null && !isFinite(Number(score))) {
        if (skelRow && skelRow.parentNode) skelRow.parentNode.removeChild(skelRow);
        renderErrorRow(function () { searchCity(city); });
        return;
      }
      var name = data.name != null ? data.name : (city && city.name) || '';
      var pct = score != null ? Math.max(0, Math.min(100, Number(score))) : 0;
      var lineFact = lineFactText(data.dominant_line);
      var bodyHtml = lineFact === '' ? '' :
        '<p class="aw-astro__unit-body">' + esc(lineFact || (ctx.i18n && ctx.i18n.sinLinea) || SIN_LINEA_FALLBACK) + '</p>';
      var row = document.createElement('article');
      row.className = 'aw-astro__unit aw-astro__city-result';
      row.innerHTML =
        '<h3 class="aw-astro__unit-anchor">' + esc(name) + '</h3>' +
        '<div class="aw-astro__stats" data-aw-astro-city-stat>' +
        '<div class="aw-astro__stat"><span class="aw-astro__stat-num">' + (score != null ? esc(String(Math.round(Number(score)))) : '') + '</span>' +
        '<span class="aw-astro__bar"><span class="aw-astro__bar-fill" style="width:' + pct + '%"></span></span></div></div>' +
        bodyHtml;
      // Land in the skeleton's slot (zero layout shift), else append.
      if (skelRow && skelRow.parentNode) skelRow.parentNode.replaceChild(row, skelRow);
      else resultsEl.appendChild(row);
      // Suspense: the number counts up and the bar grows by scaleX.
      if (suspenseOn(rootEl) && !prefersReduced()) {
        var numEl = row.querySelector('.aw-astro__stat-num');
        var fillEl = row.querySelector('.aw-astro__bar-fill');
        if (numEl && score != null) countUpEl(numEl, Math.round(Number(score)), 550);
        if (fillEl) growBar(fillEl);
      }
      maybeSearchUnit(row, city, data);
      if (score != null) {
        var pickedName = (city && city.name) || name;
        var session = sessionFor(rootEl);
        // Area line (P4): one persistent node, overwritten with the last
        // city; once painted it owns "brújula de objetivos" and the bridge
        // close switches to its no-brújula variant in the same tick.
        var areaEl = rootEl.querySelector('[data-aw-astro-area-line]');
        if (areaEl) {
          var areaTxt = pickAreaLine(pickedName, data, ctx.i18n, session.brujulaShown === true);
          if (areaTxt) {
            areaEl.textContent = areaTxt;
            areaEl.removeAttribute('hidden');
            areaSlot(rootEl, 'fill');
            session.areaLineShown = true;
            if (!areaEvtFired) { areaEvtFired = true; pushEvent('astro_area_line_shown'); } // structure only
          } else {
            areaSlot(rootEl, 'release');
          }
        }
        // Receipt refresh (P1): the CHOSEN autocomplete name (never the API
        // echo), typing order, page state only, never analytics.
        session.searched.push(pickedName);
        renderBridgeReceipt(rootEl, ctx.i18n, session.receipt.summary, session.receipt.picked, session.receipt.doors, session);
      }
    }

    // The input is never disabled (disabling steals focus and screen-reader
    // context): `busy` + aria-busy on the results region gate re-entry.
    function setBusy(b) {
      busy = b;
      resultsEl.setAttribute('aria-busy', b ? 'true' : 'false');
    }
    // Fixed-height skeleton inserted the moment a city is picked: the wait
    // (~2,3 s, server semaphore of 1) happens inside a visible row instead
    // of a frozen input, and the final row lands with zero layout shift.
    function insertSkeleton(city) {
      var skel = document.createElement('article');
      skel.className = 'aw-astro__unit aw-astro__city-result aw-astro__city-skel';
      skel.innerHTML =
        '<h3 class="aw-astro__unit-anchor">' + esc((city && city.name) || '') + '</h3>' +
        '<span class="aw-astro__skel-bar"></span>' +
        '<span class="aw-astro__skel-bar"></span>' +
        '<span class="aw-astro__skel-bar"></span>' +
        '<p class="aw-astro__city-status" data-aw-astro-city-status hidden></p>';
      // Micro-theater (suspense only): one line, both works named without
      // order — the endpoint scores and measures in its own sequence.
      if (suspenseOn(rootEl) && ctx && ctx.i18n && ctx.i18n.citySearching) {
        var st = skel.querySelector('[data-aw-astro-city-status]');
        if (st) {
          st.textContent = String(ctx.i18n.citySearching).replace(/\{ciudad\}/g, (city && city.name) || '');
          st.hidden = false;
        }
      }
      resultsEl.appendChild(skel);
      areaSlot(rootEl, 'reserve'); // the area line's space exists before the fetch resolves
      return skel;
    }

    /* E25: under the FIRST searched city whose dominant line is inside the
       1.100 km radius and drawn on the map, render ONE readable unit from
       the astrologer-approved library (body verbatim, anchor adapted with
       the gradient clause). Max one per session. */
    function maybeSearchUnit(row, city, data) {
      if (!suspenseOn(rootEl)) return;
      try { if (global.sessionStorage.getItem('aw_astro_srch_unit')) return; } catch (e) {}
      var dl = data && data.dominant_line;
      if (!dl || !dl.planet_es || !dl.angle || dl.distance_km == null) return;
      if (Number(dl.distance_km) > 1100) return; // astrologer guard: never feature beyond the radius
      if (NON_DRAWN_BODIES[slug(dl.planet_es)]) return;
      var tpl = ctx && ctx.i18n && ctx.i18n.searchUnitAnchorTpl;
      var unitKey = slug(dl.planet_es) + '_' + String(dl.angle).toLowerCase();
      var entry = lastLib && lastLib.units && lastLib.units[unitKey];
      if (!tpl || !entry || !entry.body) return;
      var anchor = fillTpl(tpl, {
        planeta: T(dl.planet_es),
        angulo: angleLabel(dl.angle),
        km: kmText(dl.distance_km, dl.angle),
        ciudad: (city && city.name) || ''
      });
      // titular support inherited from the line units (inert until the library carries titulares)
      var sTitular = entry.titular != null && String(entry.titular).trim() ? String(entry.titular).trim() : '';
      row.insertAdjacentHTML('beforeend',
        '<div class="aw-astro__search-unit">' +
        (sTitular
          ? '<h4 class="aw-astro__unit-titular">' + esc(sTitular) + '</h4>' +
            '<p class="aw-astro__unit-body">' + esc(entry.body) + '</p>' +
            '<p class="aw-astro__unit-fact">' + anchor + '</p>' + astroGlosaHtml({ key: unitKey, params: { km: dl.distance_km } })
          : '<h4 class="aw-astro__unit-anchor">' + anchor + '</h4>' +
            '<p class="aw-astro__unit-body">' + esc(entry.body) + '</p>') +
        '</div>');
      try { global.sessionStorage.setItem('aw_astro_srch_unit', '1'); } catch (e) {}
    }
    function searchCity(city, skelRow) {
      if (busy || state.capped()) { if (skelRow && skelRow.parentNode) skelRow.parentNode.removeChild(skelRow); areaSlot(rootEl, 'release'); return; }
      setBusy(true);
      var skel = skelRow || insertSkeleton(city);
      var payload = {};
      for (var k in ctx.birthPayload) { if (Object.prototype.hasOwnProperty.call(ctx.birthPayload, k)) payload[k] = ctx.birthPayload[k]; }
      payload.city = { name: city.name, lat: city.lat, lon: city.lon };
      fetch(ctx.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit'
      }).then(function (r) {
        if (!r.ok) { var e = new Error('http'); e.status = r.status; throw e; }
        return r.json();
      }).then(function (data) {
        setBusy(false);
        renderCityResult(city, data, skel);
        state.record();
        pushEvent('astro_city_search'); // event name only, never city/score
        pushEvent('astro_city_result');
        updateCapLeft();
        applyCapIfNeeded();
      }).catch(function () {
        setBusy(false);
        if (skel && skel.parentNode) skel.parentNode.removeChild(skel);
        pushEvent('astro_city_error');
        renderErrorRow(function () { searchCity(city); });
      });
    }

    function pickSuggestion(item) {
      if (!item) return;
      cityInput.value = item.text;
      clearSuggest();
      if (!item.id || !places) return;
      if (busy || state.capped()) return;
      // Skeleton appears at the pick itself (inside the 500 ms input
      // window), covering BOTH the /details lookup and the score call.
      var skel = insertSkeleton({ name: item.name || item.text });
      places.details(item.id)
        .then(function (d) {
          if (d && d.lat != null && d.lng != null) {
            searchCity({ name: item.name || item.text, lat: Number(d.lat), lon: Number(d.lng) }, skel);
          } else {
            // /details replied but without usable coordinates: silent
            // failure was the old behavior, now surfaced as a retry-able row.
            if (skel.parentNode) skel.parentNode.removeChild(skel);
            renderErrorRow(function () { pickSuggestion(item); });
          }
        })
        .catch(function () {
          if (skel.parentNode) skel.parentNode.removeChild(skel);
          renderErrorRow(function () { pickSuggestion(item); });
        });
    }

    cityInput.addEventListener('input', function () {
      var q = cityInput.value.trim();
      if (acTimer) clearTimeout(acTimer);
      if (q.length < 3) { clearSuggest(); return; }
      acTimer = setTimeout(function () { fetchAutocomplete(q); }, 350);
    });
    cityInput.addEventListener('blur', function () { setTimeout(clearSuggest, 150); });
    // mousedown (not click) so the pick lands before the input's blur fires
    suggestEl.addEventListener('mousedown', function (e) {
      var li = e.target.closest('[data-i]'); if (!li) return;
      e.preventDefault();
      pickSuggestion(acItems[parseInt(li.getAttribute('data-i'), 10)]);
    });
    // click too: assistive tech can fire click without a mousedown; after
    // a pick acItems is cleared, so a duplicate invocation no-ops.
    suggestEl.addEventListener('click', function (e) {
      var li = e.target.closest('[data-i]'); if (!li) return;
      pickSuggestion(acItems[parseInt(li.getAttribute('data-i'), 10)]);
    });
    cityInput.addEventListener('keydown', function (e) {
      if (suggestEl.hidden || !acItems.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); moveActive(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(-1); }
      else if (e.key === 'Enter') {
        if (acActive > -1) { e.preventDefault(); pickSuggestion(acItems[acActive]); }
      } else if (e.key === 'Escape') { clearSuggest(); }
    });
  }

  /* Lo que el resultado enseña de la carta, para el gate de email (11-sep). La
     serie de emails lo repite («tu brújula ya apuntó a una franja de {continente}»),
     así que solo sale lo que render() pinta: misma selección y mismas llamadas, sin
     tocar el DOM. La ciudad, con la misma condición que su cifra; los tres
     continentes de siempre (bienestar se pinta desde el 18-sep pero no viaja a Klaviyo), solo si el candado de la brújula se pinta con sus filas de zona; la
     fricción, solo si se pinta la puerta de zona. Un valor vacío o «Otros» (el
     comodín de la API) no sale. Nunca un dato de nacimiento. */
  function gateProps(summary, lib, rootEl, i18n) {
    var out = {};
    if (!summary) return out;
    i18n = i18n || {};
    function put(k, v) {
      v = v == null ? '' : String(v).trim();
      // En el idioma de la página (revisión de código 2026-10-21): ciudad, país y continentes llegan en español de la API.
      if (v && norm(v) !== 'otros') out[k] = T(v);
    }
    var tc = summary.top_city || {};
    if (tc.name != null && tc.score != null && isFinite(Number(tc.score))) {
      put('calc_astro_city', tc.name);
      if (out.calc_astro_city) put('calc_astro_country', tc.country_es);
    }
    var picked = selectAstroUnits(summary, lib);
    var readableCount = picked.length + (pickBaseUnit(summary, lib) ? 1 : 0);
    var topHtml = suspenseOn(rootEl) ? topCardHtml(summary, i18n) : '';
    var doors = selectAstroDoors(summary, readableCount, brujulaOkFor(i18n, picked, summary), topHtml !== '');
    if (doors.indexOf('zone') > -1 && /\{continente\}/.test(i18n.lockedZoneTpl || '')) {
      put('calc_astro_friccion', summary.challenge_zone.continent_es);
    }
    var rows = doors.indexOf('brujula') > -1 ? brujulaRows(i18n, picked, summary) : [];
    var zones = rows.length && rows[0].zona ? areaZones(summary) : null;
    if (zones) {
      put('calc_astro_amor', zones.amor);
      put('calc_astro_carrera', zones.carrera);
      put('calc_astro_recursos', zones.recursos);
    }
    return out;
  }

  var api = {
    selectAstroUnits: selectAstroUnits, selectAstroDoors: selectAstroDoors, brujulaRows: brujulaRows, brujulaOkFor: brujulaOkFor, brujulaAnchor: brujulaAnchor, pickedLinePlanets: pickedLinePlanets, BRUJULA_ROWS: BRUJULA_ROWS, pickBaseUnit: pickBaseUnit, citySearchState: citySearchState,
    areaZones: areaZones, chartSeed: chartSeed, zonaLines: zonaLines, gateProps: gateProps,
    pickAnchor: pickAnchor, pickBody: pickBody, lineFactText: lineFactText, topCardHtml: topCardHtml, pickAreaLine: pickAreaLine, capRecapText: capRecapText,
    buildReceiptItems: buildReceiptItems, pickBridgeEcho: pickBridgeEcho, joinList: joinList, unitLineName: unitLineName, fillText: fillText,
    statLabelFor: statLabelFor, baseAnchorHtml: baseAnchorHtml,
    buyReady: buyReady, buyHead: buyHead, buyPanelHtml: buyPanelHtml,
    _norm: norm, _slug: slug, _fmtKm: fmtKm, _fmtDistance: fmtDistance, _fmtDistanceEn: fmtDistanceEn
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  if (typeof global.window !== 'undefined') {
    global.AWAstroTeaser = api;
    global.AWAstroTeaser.render = render;
    global.AWAstroTeaser.initCitySearch = initCitySearch;
    global.AWAstroTeaser.renderBridgeReceipt = renderBridgeReceipt;
    global.AWAstroTeaser.resetSession = resetSession;
    global.AWAstroTeaser.installSeenObservers = installSeenObservers;
  }
})(typeof window !== 'undefined' ? window : globalThis);
